/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

import { apiErr } from "@/lib/utils";
import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import { applyTheme, POA_ENABLED, TIPI_LOG, ENTITA_LOG } from "./AppCore";
import { VVF_DATA, VVF_REGIONI, getComandi, getSedi } from "../data/vvfData";
import { loadXLSX, useSelezioneMultipla, CheckboxTutte, CheckboxRiga, BarraAzioniMassive, eseguiAzioneMassiva, fmtCognome } from "./shared";
import ReportView from "./ReportView";
import confirmDialog from "./confirmDialog";
import {
  LayoutDashboard, Users, ClipboardList, FileText,
  Settings, ArrowLeft, Trash2, Shield, ShieldOff, UserCheck, UserX,
  RefreshCw, Printer, Search, ChevronDown, ChevronUp, ChevronLeft, Palette,
  Plus, Pencil, X, Check, CheckCircle, BarChart2, Menu, Target, AlertTriangle, MapPin, Building2, Lock, Landmark,
  Upload, FileDown, KeyRound, Radio, Bell, Pin, EyeOff, Eye
} from "lucide-react";
import { TIPI_AVVISO } from "./AvvisiOperatore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const createAdminApi = (token) => {
  const h = { Authorization: `Bearer ${token}` };
  return {
    getStats:        ()           => axios.get(`${API}/admin/stats`, { headers: h }),
    getSecurityTraffic: ()        => axios.get(`${API}/admin/security/traffic`, { headers: h }),
    getUtenti:       ()           => axios.get(`${API}/admin/utenti`, { headers: h }),
    setRuolo:        (id, ruolo)  => axios.put(`${API}/admin/utenti/${id}/ruolo`, null, { params: { ruolo }, headers: h }),
    toggleAttivo:    (id, attivo) => axios.put(`${API}/admin/utenti/${id}/attivo`, null, { params: { attivo }, headers: h }),
    deleteUtente:         (id)         => axios.delete(`${API}/admin/utenti/${id}`, { headers: h }),
    resetPasswordUtente:  (id)         => axios.post(`${API}/admin/utenti/${id}/reset-password`, null, { headers: h }),
    unlockUtente:         (id)         => axios.post(`${API}/admin/utenti/${id}/sblocca`, null, { headers: h }),
    getOperazioni:   ()           => axios.get(`${API}/admin/operazioni`, { headers: h }),
    deleteOperazione:(id)         => axios.delete(`${API}/admin/operazioni/${id}`, { headers: h }),
    getLogs:         (limit, operazione_id) => axios.get(`${API}/admin/logs`, { params: { limit, ...(operazione_id ? { operazione_id } : {}) }, headers: h }),
    // Logs admin paginati server-side
    // params: { operazione_id, page, page_size, tipo, entita, operatore_email, data_da, data_a, q }
    getLogsPaginated: (params)              => axios.get(`${API}/admin/logs`, { params: { paginated: true, page_size: 50, ...params }, headers: h }),
    // Errori runtime del frontend (raccolti da POST /errori, pubblica)
    getErrori:       (params)     => axios.get(`${API}/admin/errori`, { params, headers: h }),
    setErroreRisolto:(impronta, risolto) => axios.put(`${API}/admin/errori/${impronta}`, { risolto }, { headers: h }),
    deleteErrore:    (impronta)   => axios.delete(`${API}/admin/errori/${impronta}`, { headers: h }),
    // Chiavi ingest radio (NMEA/LRRP) per-comando
    getIngestKeys:    ()        => axios.get(`${API}/admin/ingest-keys`, { headers: h }),
    createIngestKey:  (nome)    => axios.post(`${API}/admin/ingest-keys`, { nome }, { headers: h }),
    revocaIngestKey:  (id)      => axios.post(`${API}/admin/ingest-keys/${id}/revoca`, null, { headers: h }),
    deleteIngestKey:  (id)      => axios.delete(`${API}/admin/ingest-keys/${id}`, { headers: h }),
    getAvvisi:        ()        => axios.get(`${API}/admin/avvisi`, { headers: h }),
    createAvviso:     (data)    => axios.post(`${API}/admin/avvisi`, data, { headers: h }),
    updateAvviso:     (id, data) => axios.put(`${API}/admin/avvisi/${id}`, data, { headers: h }),
    deleteAvviso:     (id)      => axios.delete(`${API}/admin/avvisi/${id}`, { headers: h }),
    getImpostazioni:    ()              => axios.get(`${API}/admin/impostazioni`, { headers: h }),
    saveImpostazioni:   (data)          => axios.put(`${API}/admin/impostazioni`, data, { headers: h }),
    setupAdmin:         (setup_key)     => axios.post(`${API}/admin/setup`, { setup_key }, { headers: h }),
    getTas2Personale:   (params)        => axios.get(`${API}/admin/tas2-personale`, { params, headers: h }),
    createTas2:         (data)          => axios.post(`${API}/admin/tas2-personale`, data, { headers: h }),
    importTas2:         (data)          => axios.post(`${API}/admin/tas2-personale/import`, data, { headers: h }),
    updateTas2:         (id, data)      => axios.put(`${API}/admin/tas2-personale/${id}`, data, { headers: h }),
    deleteTas2:         (id)            => axios.delete(`${API}/admin/tas2-personale/${id}`, { headers: h }),
    getSediVvf:         (params)        => axios.get(`${API}/admin/sedi-vvf`, { params, headers: h }),
    createSediVvf:      (data)          => axios.post(`${API}/admin/sedi-vvf`, data, { headers: h }),
    updateSediVvf:      (id, data)      => axios.put(`${API}/admin/sedi-vvf/${id}`, data, { headers: h }),
    deleteSediVvf:      (id)            => axios.delete(`${API}/admin/sedi-vvf/${id}`, { headers: h }),
    getEnti:            (params)        => axios.get(`${API}/admin/enti`, { params, headers: h }),
    createEnte:         (data)          => axios.post(`${API}/admin/enti`, data, { headers: h }),
    updateEnte:         (id, data)      => axios.put(`${API}/admin/enti/${id}`, data, { headers: h }),
    deleteEnte:         (id)            => axios.delete(`${API}/admin/enti/${id}`, { headers: h }),
    getReport:          (p = {})        => axios.get(`${API}/admin/report`, { params: { ...(p.da ? { da: p.da } : {}), ...(p.a ? { a: p.a } : {}) }, headers: h }),
    // Fase 2 - Validazione POA / Ritrovamenti storici
    getRitrovamenti:        (params)      => axios.get(`${API}/ritrovamenti-storici`, { params, headers: h }),
    getRitrovamento:        (id)          => axios.get(`${API}/ritrovamenti-storici/${id}`, { headers: h }),
    getRitrovamentiStats:   (params)      => axios.get(`${API}/ritrovamenti-storici/stats/dashboard`, { params, headers: h }),
    getStatoCalibrazione:   ()            => axios.get(`${API}/ritrovamenti-storici/stats/calibrazione-locale`, { headers: h }),
  };
};

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, color = "orange", onClick }) => {
  const colors = {
    orange: "border-orange-500 text-orange-400",
    blue:   "border-blue-500 text-blue-400",
    green:  "border-green-500 text-green-400",
    red:    "border-red-500 text-red-400",
    purple: "border-purple-500 text-purple-400",
  };
  const clickable = !!onClick;
  return (
    <div
      className={`tactical-card p-4 border-l-4 ${colors[color]}${clickable ? " cursor-pointer hover:bg-slate-700/60 transition-colors" : ""}`}
      onClick={onClick}
      title={clickable ? `Vai a ${label}` : undefined}
    >
      <div className="text-2xl font-bold text-white">{value ?? "–"}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
      {clickable && <div className="text-xs text-slate-500 mt-1">→ vedi sezione</div>}
    </div>
  );
};

// ── Badge ─────────────────────────────────────────────────────────────────────
const RUOLO_BADGE_CLS = {
  admin:     "bg-orange-500/20 text-orange-400 border border-orange-500/40",
  rop:       "bg-blue-500/20 text-blue-400 border border-blue-500/40",
  ror:       "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40",
  ron:       "bg-purple-500/20 text-purple-400 border border-purple-500/40",
  dir_reg:   "bg-teal-500/20 text-teal-400 border border-teal-500/40",
  con:       "bg-indigo-500/20 text-indigo-400 border border-indigo-500/40",
  com_prov:  "bg-sky-500/20 text-sky-400 border border-sky-500/40",
  operatore: "bg-slate-700 text-slate-300 border border-slate-600",
  attivo:    "bg-green-500/20 text-green-400 border border-green-500/40",
  inattivo:  "bg-red-500/20 text-red-400 border border-red-500/40",
};
const Badge = ({ text, type }) => {
  const cls = RUOLO_BADGE_CLS[type] || "bg-slate-700 text-slate-300";
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{text}</span>;
};

const RUOLI_LISTA = [
  { value: "operatore", label: "Operatore" },
  { value: "rop",       label: "ROP — Resp. Operativo Provinciale" },
  { value: "ror",       label: "ROR — Resp. Operativo Regionale" },
  { value: "ron",       label: "RON — Resp. Operativo Nazionale" },
  { value: "dir_reg",   label: "DIR REG — Direzione Regionale (sola lettura)" },
  { value: "con",       label: "CON — Centro Operativo Nazionale (sola lettura)" },
  { value: "com_prov",  label: "COM PROV — Comando Provinciale (sola lettura)" },
  { value: "admin",     label: "Amministratore" },
];

// ── Filter helpers (stessa struttura di App.js) ───────────────────────────────
const FilterBar = ({ onReset, children }) => (
  <div className="tactical-card p-3 mb-4">
    <div className="flex flex-wrap gap-2 items-end">
      {children}
      <button
        className="btn btn-secondary btn-sm py-1 px-3 text-xs flex items-center gap-1 self-end"
        onClick={onReset}
        title="Azzera filtri"
      >
        <X size={12} /> Azzera
      </button>
    </div>
  </div>
);
const FilterInput = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div className="flex flex-col gap-1">
    {label && <label className="text-xs text-slate-400">{label}</label>}
    <div className="relative">
      {type === "text" && <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />}
      <input
        type={type}
        className="form-input py-1 text-xs"
        style={{ minWidth: 140, paddingLeft: type === "text" ? "2rem" : undefined }}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  </div>
);
const FilterSelect = ({ label, value, onChange, options, placeholder = "Tutti" }) => (
  <div className="flex flex-col gap-1">
    {label && <label className="text-xs text-slate-400">{label}</label>}
    <select
      className="form-select py-1 text-xs"
      style={{ minWidth: 130 }}
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
          {typeof o === "string" ? o : o.label}
        </option>
      ))}
    </select>
  </div>
);

// ── Monitoraggio traffico (anti-DDoS) ─────────────────────────────────────────
// Polla lo snapshot live ogni 15s. In allerta mostra un banner rosso; altrimenti
// una riga discreta col rate corrente. La storia degli allarmi resta nei Logs
// (eventi SECURITY scritti dal backend).
const TrafficMonitorCard = ({ api, onNavigate }) => {
  const [t, setT] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.getSecurityTraffic(); setT(data); }
    catch { /* silenzioso: il widget è informativo, non bloccante */ }
  }, [api]);
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  if (!t) return null;

  if (t.alerting) {
    return (
      <div className="mb-4 p-4 rounded-lg border border-red-500 bg-red-500/10">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle size={18} className="text-red-400" />
          <span className="font-bold text-red-400">Possibile attacco / picco di traffico anomalo</span>
        </div>
        <p className="text-sm text-slate-300">
          <strong className="text-white">{t.req_last_min}</strong> richieste nell'ultimo minuto
          (soglia {t.threshold_global}; per-IP {t.threshold_per_ip}) da {t.unique_ips} IP.
        </p>
        {t.top_ips?.length > 0 && (
          <p className="text-xs text-slate-400 mt-1 font-mono">
            Top IP: {t.top_ips.slice(0, 3).map(x => `${x.ip} (${x.count})`).join(" · ")}
          </p>
        )}
        <p className="text-xs text-red-300 mt-2">
          Valuta l'attivazione di <strong>Under Attack Mode</strong> su Railway (solo dominio frontend, non l'API).
          L'evento è registrato nei <button className="underline hover:text-red-200" onClick={() => onNavigate && onNavigate('logs')}>Logs</button>.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-4 flex items-center gap-2 text-xs text-slate-400">
      <Shield size={13} className="text-green-500" />
      Traffico normale · <strong className="text-slate-300">{t.req_last_min}</strong> req/min · {t.unique_ips} IP
      <span className="text-slate-600">(soglia allerta {t.threshold_global}/min)</span>
    </div>
  );
};

// ── Dashboard Tab ─────────────────────────────────────────────────────────────
const DashboardTab = ({ api, onNavigate }) => {
  const [stats, setStats] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.getStats(); setStats(data); }
    catch { toast.error("Errore caricamento statistiche"); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white">Panoramica Sistema</h2>
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /></button>
      </div>
      <TrafficMonitorCard api={api} onNavigate={onNavigate} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Utenti Totali"       value={stats?.utenti_totali}       color="blue"   onClick={onNavigate ? () => onNavigate('utenti') : undefined} />
        <StatCard label="Amministratori"       value={stats?.utenti_admin}        color="orange" />
        <StatCard label="Operazioni Totali"    value={stats?.operazioni_totali}   color="purple" onClick={onNavigate ? () => onNavigate('interventi') : undefined} />
        <StatCard label="Operazioni Attive"    value={stats?.operazioni_attive}   color="green" />
        <StatCard label="Soccorritori Totali"  value={stats?.soccorritori_totali} color="blue" />
        <StatCard label="Squadre Totali"       value={stats?.squadre_totali}      color="orange" />
        <StatCard label="Missioni Totali"      value={stats?.missioni_totali}     color="red" />
        <StatCard label="Log di Sistema"       value={stats?.logs_totali}         color="purple" onClick={onNavigate ? () => onNavigate('logs') : undefined} />
      </div>
    </div>
  );
};

// ── Utenti Tab ────────────────────────────────────────────────────────────────

/** Dropdown "Azioni" per una singola riga utente */
const AzioniUtente = ({ u, operatoreId, onRuolo, onToggle, onReset, onDelete, onUnlock, onDettaglio }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, right: 0 });
  const btnRef          = useRef(null);
  const isSelf = u.id === operatoreId;
  const isLocked = u.locked_until && new Date(u.locked_until) > new Date();

  if (isSelf) return <span className="text-xs text-slate-500 italic">Tu</span>;

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const menuH = Math.min(window.innerHeight * 0.8, 480); // stima altezza menu
      const top = spaceBelow >= menuH ? r.bottom + 4 : Math.max(8, r.top - menuH - 4);
      setPos({ top, right: window.innerWidth - r.right });
    }
    setOpen(v => !v);
  };

  const btn = (label, icon, onClick, danger = false) => (
    <button
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded transition-colors text-left
        ${danger ? "text-red-400 hover:bg-red-500/20" : "text-slate-300 hover:bg-slate-700 hover:text-white"}`}
      onClick={() => { setOpen(false); onClick(); }}
    >
      {icon}{label}
    </button>
  );

  return (
    <div className="relative">
      <button
        ref={btnRef}
        className="btn btn-secondary btn-sm flex items-center gap-1"
        onClick={handleOpen}
      >
        Azioni <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 py-1 max-h-[80vh] overflow-y-auto"
            style={{ top: pos.top, right: pos.right }}
          >
            {btn("Visualizza profilo",  <UserCheck size={14} />,  () => onDettaglio(u))}
            <div className="my-1 border-t border-slate-700" />
            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-medium">Imposta ruolo</div>
            {RUOLI_LISTA.map(r => {
              const isCurrent = u.ruolo === r.value;
              return (
                <button
                  key={r.value}
                  disabled={isCurrent}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded transition-colors text-left
                    ${isCurrent
                      ? "text-orange-400 bg-slate-700/50 cursor-default font-semibold"
                      : "text-slate-300 hover:bg-slate-700 hover:text-white"}`}
                  onClick={() => { if (!isCurrent) { setOpen(false); onRuolo(u.id, r.value); } }}
                >
                  <Shield size={14} />
                  <span className="flex-1 truncate">{r.label}</span>
                  {isCurrent && <span className="text-[10px] uppercase">attuale</span>}
                </button>
              );
            })}
            <div className="my-1 border-t border-slate-700" />
            {u.attivo !== false
              ? btn("Disattiva account", <UserX size={14} />,    () => onToggle(u.id, false))
              : btn("Attiva account",    <UserCheck size={14} />, () => onToggle(u.id, true))
            }
            {btn("Invia reset password", <RefreshCw size={14} />, () => onReset(u))}
            {isLocked && btn("Sblocca account", <UserCheck size={14} />, () => onUnlock(u))}
            <div className="my-1 border-t border-slate-700" />
            {btn("Elimina utente",       <Trash2 size={14} />,    () => onDelete(u.id, u.email), true)}
          </div>
        </>
      )}
    </div>
  );
};

/** Modal dettaglio profilo completo */
const DettaglioUtenteModal = ({ utente, operatoreId, onClose, onRuolo, onToggle, onReset, onDelete, onUnlock, onReload }) => {
  if (!utente) return null;
  const isLocked = utente.locked_until && new Date(utente.locked_until) > new Date();
  const isSelf = utente.id === operatoreId;

  const InfoRow = ({ label, children }) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2 border-b border-slate-700/50 last:border-0">
      <span className="text-xs text-slate-500 w-36 flex-shrink-0">{label}</span>
      <span className="text-sm text-slate-200">{children}</span>
    </div>
  );

  const handleAction = async (fn) => { await fn(); onReload(); };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="tactical-card w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-500/20 border border-orange-500/40 flex items-center justify-center text-orange-400 font-bold text-lg">
              {(utente.nome?.[0] || "?").toUpperCase()}
            </div>
            <div>
              <div className="text-white font-bold">{utente.nome} {utente.cognome}</div>
              <div className="text-xs text-slate-400 font-mono">{utente.email}</div>
            </div>
          </div>
          <button className="text-slate-400 hover:text-white" onClick={onClose}><X size={18} /></button>
        </div>

        {/* Dati */}
        <div className="mb-5">
          <InfoRow label="ID">
            <span className="font-mono text-xs text-slate-400 break-all">{utente.id}</span>
          </InfoRow>
          <InfoRow label="Ruolo">
            <Badge text={utente.ruolo || "operatore"} type={utente.ruolo || "operatore"} />
          </InfoRow>
          <InfoRow label="Stato account">
            <Badge text={utente.attivo !== false ? "Attivo" : "Inattivo"} type={utente.attivo !== false ? "attivo" : "inattivo"} />
          </InfoRow>
          {isLocked && (
            <InfoRow label="Blocco accesso">
              <span className="flex items-center gap-1 text-red-400 text-xs">
                <X size={13} /> Bloccato fino a {new Date(utente.locked_until).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
              </span>
            </InfoRow>
          )}
          <InfoRow label="Email verificata">
            {utente.email_verificata
              ? <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle size={13} /> Verificata</span>
              : <span className="flex items-center gap-1 text-yellow-400 text-xs"><X size={13} /> Non verificata</span>}
          </InfoRow>
          <InfoRow label="Registrato il">
            {utente.created_at
              ? new Date(utente.created_at).toLocaleString("it-IT", { dateStyle: "long", timeStyle: "short" })
              : "–"}
          </InfoRow>
        </div>

        {/* Azioni */}
        {!isSelf && (
          <div className="space-y-2 pt-4 border-t border-slate-700">
            <div className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Azioni</div>
            {/* Cambio ruolo */}
            <div className="flex gap-2 items-center">
              <select
                className="form-select text-xs flex-1"
                defaultValue={utente.ruolo || "operatore"}
                id={`ruolo-sel-${utente.id}`}
              >
                {RUOLI_LISTA.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <button className="btn btn-secondary text-xs whitespace-nowrap"
                onClick={() => {
                  const sel = document.getElementById(`ruolo-sel-${utente.id}`);
                  if (sel) handleAction(() => onRuolo(utente.id, sel.value));
                }}>
                <Shield size={13} /> Applica
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {utente.attivo !== false
                ? <button className="btn btn-secondary text-xs flex items-center gap-1 justify-center"
                    onClick={() => handleAction(() => onToggle(utente.id, false))}>
                    <UserX size={13} /> Disattiva
                  </button>
                : <button className="btn btn-secondary text-xs flex items-center gap-1 justify-center"
                    onClick={() => handleAction(() => onToggle(utente.id, true))}>
                    <UserCheck size={13} /> Attiva
                  </button>
              }
              <button className="btn btn-secondary text-xs flex items-center gap-1 justify-center col-span-2"
                onClick={() => onReset(utente)}>
                <RefreshCw size={13} /> Invia Reset Password
              </button>
              {isLocked && (
                <button className="btn text-xs flex items-center gap-1 justify-center col-span-2 bg-yellow-500/20 text-yellow-400 border border-yellow-500/40 hover:bg-yellow-500/40"
                  onClick={() => handleAction(() => onUnlock(utente))}>
                  <UserCheck size={13} /> Sblocca Account
                </button>
              )}
              <button
                className="btn text-xs flex items-center gap-1 justify-center col-span-2 bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/40"
                onClick={() => { onDelete(utente.id, utente.email); onClose(); }}>
                <Trash2 size={13} /> Elimina Utente
              </button>
            </div>
          </div>
        )}
        {isSelf && (
          <div className="pt-4 border-t border-slate-700 text-xs text-slate-500 italic">
            Questo è il tuo account — alcune azioni non sono disponibili.
          </div>
        )}
      </div>
    </div>
  );
};

const UtentiTab = ({ api, operatoreId }) => {
  const [utenti, setUtenti]       = useState([]);
  const [search, setSearch]       = useState("");
  const [filtroRuolo, setFiltroRuolo] = useState("");
  const [filtroAttivo, setFiltroAttivo] = useState("");
  const [loading, setLoading]     = useState(false);
  const [dettaglio, setDettaglio] = useState(null); // utente aperto nel modal

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.getUtenti(); setUtenti(data); }
    catch { toast.error("Errore caricamento utenti"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Aggiorna lo stato del modal quando la lista si ricarica
  useEffect(() => {
    if (dettaglio) {
      const aggiornato = utenti.find(u => u.id === dettaglio.id);
      if (aggiornato) setDettaglio(aggiornato);
    }
  }, [utenti]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRuolo = async (id, ruolo) => {
    try { await api.setRuolo(id, ruolo); toast.success("Ruolo aggiornato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  const handleToggle = async (id, attivo) => {
    try { await api.toggleAttivo(id, attivo); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  const handleDelete = async (id, email) => {
    if (!(await confirmDialog({ title: "Elimina utente", message: `Eliminare l'utente ${email}? Questa azione è irreversibile.`, danger: true, confirmLabel: "Elimina" }))) return;
    try { await api.deleteUtente(id); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  const handleReset = async (u) => {
    if (!(await confirmDialog({ title: "Reset password", message: `Inviare un link di reset password a ${u.email}?`, confirmLabel: "Invia" }))) return;
    try {
      const { data } = await api.resetPasswordUtente(u.id);
      if (data.email_sent) toast.success(`Email di reset inviata a ${u.email}`);
      else toast.warning(`Token generato ma email non inviata (SMTP non configurato). Token salvato nel DB.`);
    } catch (e) { toast.error(apiErr(e, "Errore reset password")); }
  };
  const handleUnlock = async (u) => {
    try { await api.unlockUtente(u.id); toast.success(`Account ${u.email} sbloccato`); load(); }
    catch (e) { toast.error(apiErr(e, "Errore sblocco account")); }
  };

  const filtered = utenti.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q || u.email.toLowerCase().includes(q) || `${u.nome} ${u.cognome}`.toLowerCase().includes(q);
    const matchRuolo  = !filtroRuolo  || (u.ruolo || "operatore") === filtroRuolo;
    const matchAttivo = !filtroAttivo || (filtroAttivo === "attivo" ? u.attivo !== false : u.attivo === false);
    return matchSearch && matchRuolo && matchAttivo;
  }).sort((a, b) => {
    // Ordine alfabetico per cognome, poi nome (case-insensitive, locale IT)
    const byCognome = (a.cognome || "").localeCompare(b.cognome || "", "it", { sensitivity: "base" });
    return byCognome !== 0 ? byCognome : (a.nome || "").localeCompare(b.nome || "", "it", { sensitivity: "base" });
  });

  const sharedActions = { onRuolo: handleRuolo, onToggle: handleToggle, onReset: handleReset, onDelete: handleDelete, onUnlock: handleUnlock };

  // Il proprio account resta fuori dalla selezione: disattivarsi o cancellarsi
  // in mezzo a un'azione massiva significa perdere l'accesso da amministratore
  // nello stesso gesto con cui si stava amministrando.
  const sel = useSelezioneMultipla(utenti, filtered, (u) => u.id !== operatoreId);

  const bulkAttivo = async (attivo) => {
    const { ok, ko } = await eseguiAzioneMassiva(sel.ids, (id) => api.toggleAttivo(id, attivo));
    if (ok) toast.success(`${ok} utent${ok === 1 ? "e" : "i"} ${attivo ? "attivat" : "disattivat"}${ok === 1 ? "o" : "i"}`);
    if (ko) toast.error(`${ko} non aggiornat${ko === 1 ? "o" : "i"}`);
    sel.pulisci(); load();
  };

  const bulkDelete = async () => {
    const n = sel.count;
    if (!(await confirmDialog({
      title: "Elimina utenti",
      message: `Eliminare ${n} utent${n === 1 ? "e" : "i"}? L'azione è irreversibile.`,
      danger: true, confirmLabel: "Elimina",
    }))) return;
    const { ok, ko } = await eseguiAzioneMassiva(sel.ids, (id) => api.deleteUtente(id));
    if (ok) toast.success(`${ok} eliminat${ok === 1 ? "o" : "i"}`);
    if (ko) toast.error(`${ko} non eliminat${ko === 1 ? "o" : "i"}`);
    sel.pulisci(); load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-white">Gestione Utenti</h2>
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /></button>
      </div>
      <FilterBar onReset={() => { setSearch(""); setFiltroRuolo(""); setFiltroAttivo(""); }}>
        <FilterInput label="Cerca" value={search} onChange={setSearch} placeholder="Nome, email…" />
        <FilterSelect label="Ruolo" value={filtroRuolo} onChange={setFiltroRuolo}
          options={RUOLI_LISTA.map(r => ({ value: r.value, label: r.label }))}
          placeholder="Tutti i ruoli" />
        <FilterSelect label="Stato" value={filtroAttivo} onChange={setFiltroAttivo}
          options={[{ value: "attivo", label: "Attivi" }, { value: "inattivo", label: "Inattivi" }]}
          placeholder="Tutti gli stati" />
      </FilterBar>

      <BarraAzioniMassive selezione={sel} nomeSingolare="utente" nomePlurale="utenti">
        <button className="btn btn-secondary btn-sm" onClick={() => bulkAttivo(true)}>
          <CheckCircle size={14} /> Attiva ({sel.count})
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => bulkAttivo(false)}>
          <X size={14} /> Disattiva ({sel.count})
        </button>
        <button className="btn btn-secondary btn-sm text-red-400" onClick={bulkDelete}>
          <Trash2 size={14} /> Elimina ({sel.count})
        </button>
      </BarraAzioniMassive>

      {loading ? <div className="text-slate-400 text-sm">Caricamento...</div> : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="p-3 w-8"><CheckboxTutte selezione={sel} /></th>
                <th className="text-left p-3 text-slate-400 font-medium">Utente</th>
                <th className="text-left p-3 text-slate-400 font-medium">Email</th>
                <th className="text-left p-3 text-slate-400 font-medium">Ruolo</th>
                <th className="text-left p-3 text-slate-400 font-medium">Stato</th>
                <th className="text-left p-3 text-slate-400 font-medium">Email ✓</th>
                <th className="text-left p-3 text-slate-400 font-medium">Registrato</th>
                <th className="text-left p-3 text-slate-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} className={`border-b border-slate-800 hover:bg-slate-800/50 ${sel.isSel(u.id) ? "bg-slate-800/70" : ""}`}>
                  <td className="p-3">
                    <CheckboxRiga selezione={sel} id={u.id}
                      motivo={u.id === operatoreId ? "È il tuo account: non selezionabile per le azioni massive" : ""} />
                  </td>
                  <td className="p-3 text-white font-medium">{u.cognome} {u.nome}</td>
                  <td className="p-3 text-slate-300 font-mono text-xs">{u.email}</td>
                  <td className="p-3">
                    <Badge text={u.ruolo || "operatore"} type={u.ruolo || "operatore"} />
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col gap-1 items-start">
                      <Badge text={u.attivo !== false ? "Attivo" : "Inattivo"} type={u.attivo !== false ? "attivo" : "inattivo"} />
                      {u.locked_until && new Date(u.locked_until) > new Date() && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/40">
                          <Lock size={10} /> Bloccato
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    {u.email_verificata
                      ? <CheckCircle size={14} className="text-green-400 mx-auto" title="Email verificata" />
                      : <X size={14} className="text-yellow-500 mx-auto" title="Email non verificata" />}
                  </td>
                  <td className="p-3 text-slate-400 text-xs font-mono">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "–"}
                  </td>
                  <td className="p-3">
                    <AzioniUtente
                      u={u}
                      operatoreId={operatoreId}
                      onDettaglio={setDettaglio}
                      {...sharedActions}
                    />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-500">Nessun utente trovato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal dettaglio */}
      <DettaglioUtenteModal
        utente={dettaglio}
        operatoreId={operatoreId}
        onClose={() => setDettaglio(null)}
        onReload={load}
        {...sharedActions}
      />
    </div>
  );
};

// ── Interventi Tab ────────────────────────────────────────────────────────────
const InterventiTab = ({ api }) => {
  const [operazioni, setOperazioni] = useState([]);
  const [search, setSearch]         = useState("");
  const [dataDa, setDataDa]         = useState("");
  const [dataA, setDataA]           = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [loading, setLoading]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.getOperazioni(); setOperazioni(data); }
    catch { toast.error("Errore caricamento interventi"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, nome) => {
    if (!(await confirmDialog({ title: "Elimina intervento", message: `Eliminare l'intervento "${nome}" e tutti i dati correlati?`, danger: true, confirmLabel: "Elimina" }))) return;
    try { await api.deleteOperazione(id); toast.success("Intervento eliminato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const filtered = operazioni.filter(op => {
    const matchTesto = op.nome.toLowerCase().includes(search.toLowerCase()) ||
      op.localita.toLowerCase().includes(search.toLowerCase());
    const d = (op.data_inizio || "").slice(0, 10); // YYYY-MM-DD per confronto stringa
    const matchDa = !dataDa || (d && d >= dataDa);
    const matchA  = !dataA  || (d && d <= dataA);
    // Le righe senza `tipo` (create prima della migr. 044) sono interventi reali
    const matchTipo = !filtroTipo || (op.tipo || "INTERVENTO") === filtroTipo;
    return matchTesto && matchDa && matchA && matchTipo;
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-white">Gestione Interventi</h2>
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /></button>
      </div>
      <FilterBar onReset={() => { setSearch(""); setDataDa(""); setDataA(""); setFiltroTipo(""); }}>
        <FilterInput label="Cerca" value={search} onChange={setSearch} placeholder="Nome, località…" />
        <FilterSelect label="Tipo" value={filtroTipo} onChange={setFiltroTipo}
          options={[{ value: "INTERVENTO", label: "Intervento reale" }, { value: "ESERCITAZIONE", label: "Esercitazione" }]} />
        <FilterInput label="Da data" type="date" value={dataDa} onChange={setDataDa} />
        <FilterInput label="A data" type="date" value={dataA} onChange={setDataA} />
      </FilterBar>

      {loading ? <div className="text-slate-400 text-sm">Caricamento...</div> : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-3 text-slate-400 font-medium">Nome</th>
                <th className="text-left p-3 text-slate-400 font-medium">Tipo</th>
                <th className="text-left p-3 text-slate-400 font-medium">Località</th>
                <th className="text-left p-3 text-slate-400 font-medium">Data</th>
                <th className="text-left p-3 text-slate-400 font-medium">Stato</th>
                <th className="text-left p-3 text-slate-400 font-medium">Socc.</th>
                <th className="text-left p-3 text-slate-400 font-medium">Squadre</th>
                <th className="text-left p-3 text-slate-400 font-medium">Missioni</th>
                <th className="text-left p-3 text-slate-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(op => (
                <tr key={op.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                  <td className="p-3 text-white font-medium">{op.nome}</td>
                  <td className="p-3">
                    {/* Stessa codifica della dashboard: esercitazione = badge giallo.
                        Le righe pre-migr. 044 senza `tipo` sono interventi reali. */}
                    {op.tipo === 'ESERCITAZIONE'
                      ? <span className="badge badge-yellow text-[10px]" title="Esercitazione: i dati non confluiscono nelle statistiche POA">ESERCITAZIONE</span>
                      : <span className="text-xs text-slate-400">Intervento reale</span>}
                  </td>
                  <td className="p-3 text-slate-300">{op.localita}</td>
                  <td className="p-3 text-slate-400 font-mono text-xs">{op.data_inizio}</td>
                  <td className="p-3">
                    <Badge text={!op.chiusa ? "Attiva" : "Chiusa"} type={!op.chiusa ? "attivo" : "inattivo"} />
                  </td>
                  <td className="p-3 text-center text-slate-300">{op.num_soccorritori ?? 0}</td>
                  <td className="p-3 text-center text-slate-300">{op.num_squadre ?? 0}</td>
                  <td className="p-3 text-center text-slate-300">{op.num_missioni ?? 0}</td>
                  <td className="p-3">
                    <button className="btn btn-sm bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/40" title="Elimina" onClick={() => handleDelete(op.id, op.nome)}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-slate-500">Nessun intervento trovato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Logs Tab ──────────────────────────────────────────────────────────────────
const fmtLogTs = (ts) => {
  if (!ts) return { data: '–', ora: '–' };
  const d = new Date(ts);
  const p = n => String(n).padStart(2, '0');
  return { data: `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}`, ora: `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}` };
};

const LogsTab = ({ api, operazioneAttiva }) => {
  const [logs, setLogs]             = useState([]);
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(false);
  const [expanded, setExpanded]     = useState(null);
  const [filtroOp, setFiltroOp]     = useState(false);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEntita, setFiltroEntita] = useState("");
  const [filtroOperatore, setFiltroOperatore] = useState("");
  const [filtroIp, setFiltroIp] = useState("");
  const [filtroDataDa, setFiltroDataDa] = useState("");
  const [filtroDataA, setFiltroDataA] = useState("");
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [pages, setPages]           = useState(1);
  const PAGE_SIZE = 50;

  // Debounce ricerche testuali (evita chiamate ad ogni keystroke)
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [debouncedOperatore, setDebouncedOperatore] = useState(filtroOperatore);
  const [debouncedIp, setDebouncedIp] = useState(filtroIp);
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 400); return () => clearTimeout(t); }, [search]);
  useEffect(() => { const t = setTimeout(() => setDebouncedOperatore(filtroOperatore), 400); return () => clearTimeout(t); }, [filtroOperatore]);
  useEffect(() => { const t = setTimeout(() => setDebouncedIp(filtroIp), 400); return () => clearTimeout(t); }, [filtroIp]);

  // Reset pagina quando cambia un filtro
  useEffect(() => { setPage(1); }, [debouncedSearch, filtroOp, filtroTipo, filtroEntita, debouncedOperatore, debouncedIp, filtroDataDa, filtroDataA, operazioneAttiva]);

  const load = useCallback(async () => {
    setLoading(true);
    const opId = filtroOp && operazioneAttiva ? operazioneAttiva.id : null;
    try {
      const { data } = await api.getLogsPaginated({
        page,
        page_size: PAGE_SIZE,
        ...(opId ? { operazione_id: opId } : {}),
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
        ...(filtroTipo ? { tipo: filtroTipo } : {}),
        ...(filtroEntita ? { entita: filtroEntita } : {}),
        ...(debouncedOperatore ? { operatore_email: debouncedOperatore } : {}),
        ...(debouncedIp ? { ip: debouncedIp } : {}),
        ...(filtroDataDa ? { data_da: filtroDataDa } : {}),
        ...(filtroDataA ? { data_a: filtroDataA } : {}),
      });
      setLogs(data.data || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch { toast.error("Errore caricamento log"); }
    finally { setLoading(false); }
  }, [api, page, debouncedSearch, filtroOp, filtroTipo, filtroEntita, debouncedOperatore, debouncedIp, filtroDataDa, filtroDataA, operazioneAttiva]);

  useEffect(() => { load(); }, [load]);

  const filtered = logs;

  const tipoColor = (tipo) => {
    const map = { LOGIN: "text-green-400", LOGOUT: "text-slate-400", CREATE: "text-blue-400", UPDATE: "text-yellow-400", DELETE: "text-red-400", ASSIGN: "text-purple-400", STATUS_CHANGE: "text-orange-400", VALIDATION: "text-cyan-400", SECURITY: "text-red-500 font-bold" };
    return map[tipo] || "text-slate-400";
  };

  const handlePrint = async () => {
    // Per la stampa scarica fino a 1000 log con i filtri correnti (server-side)
    try {
      const opId = filtroOp && operazioneAttiva ? operazioneAttiva.id : null;
      const { data: pData } = await api.getLogsPaginated({
        page: 1, page_size: 1000,
        ...(opId ? { operazione_id: opId } : {}),
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
        ...(filtroTipo ? { tipo: filtroTipo } : {}),
        ...(filtroEntita ? { entita: filtroEntita } : {}),
        ...(debouncedOperatore ? { operatore_email: debouncedOperatore } : {}),
        ...(debouncedIp ? { ip: debouncedIp } : {}),
        ...(filtroDataDa ? { data_da: filtroDataDa } : {}),
        ...(filtroDataA ? { data_a: filtroDataA } : {}),
      });
      const list = pData.data || [];
      // Escape HTML: i log contengono testo inserito dagli utenti (descrizione,
      // email, entità) → senza escape sarebbe XSS nel popup di stampa (same-origin,
      // accesso a window.opener.localStorage → furto token).
      const escHtml = (x) => String(x ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const rows = list.map(l => {
        const ts = fmtLogTs(l.timestamp);
        return `<tr><td>${escHtml(ts.data)}</td><td>${escHtml(ts.ora)}</td><td>${escHtml(l.tipo)}</td><td>${escHtml(l.operatore_email||'Sistema')}</td><td>${escHtml(l.ip_address||'')}</td><td>${escHtml(l.entita_tipo)}</td><td>${escHtml(l.descrizione)}</td></tr>`;
      }).join('');
      const w = window.open('', '_blank', 'width=900,height=700');
      if (!w) { toast.error("Popup bloccato"); return; }
      w.document.write(`<html><head><title>Log Sistema TAS</title><style>body{font-family:monospace;font-size:11px;padding:20px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:4px 6px;text-align:left}th{background:#f0f0f0;font-weight:bold}tr:nth-child(even){background:#f9f9f9}h2{margin:0 0 8px}</style></head><body><h2>Log Sistema TAS Command</h2><p>Generato: ${new Date().toLocaleString('it-IT')} — ${list.length} righe</p><table><thead><tr><th>Data</th><th>Ora</th><th>Tipo</th><th>Operatore</th><th>IP</th><th>Entità</th><th>Descrizione</th></tr></thead><tbody>${rows}</tbody></table><script>setTimeout(()=>{window.print();},400);<\/script></body></html>`);
      w.document.close();
    } catch { toast.error("Errore stampa"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-white">Log di Sistema</h2>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={load} title="Ricarica"><RefreshCw size={14} /></button>
          <button className="btn btn-secondary btn-sm" onClick={handlePrint} title="Stampa"><Printer size={14} /></button>
        </div>
      </div>
      <FilterBar onReset={() => { setSearch(""); setFiltroOp(false); setFiltroTipo(""); setFiltroEntita(""); setFiltroOperatore(""); setFiltroIp(""); setFiltroDataDa(""); setFiltroDataA(""); }}>
        <FilterInput label="Cerca" value={search} onChange={setSearch} placeholder="Descrizione, nome operazione…" />
        <FilterSelect label="Tipo" value={filtroTipo} onChange={setFiltroTipo} options={TIPI_LOG} />
        <FilterSelect label="Entità" value={filtroEntita} onChange={setFiltroEntita} options={ENTITA_LOG} />
        <FilterInput label="Operatore" value={filtroOperatore} onChange={setFiltroOperatore} placeholder="email…" />
        <FilterInput label="IP" value={filtroIp} onChange={setFiltroIp} placeholder="es. 78.210.7.50" />
        <FilterInput label="Da data" type="date" value={filtroDataDa} onChange={setFiltroDataDa} />
        <FilterInput label="A data" type="date" value={filtroDataA} onChange={setFiltroDataA} />
        {operazioneAttiva && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-400">Op. attiva</label>
            <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer select-none py-1.5">
              <input type="checkbox" checked={filtroOp} onChange={e => setFiltroOp(e.target.checked)} className="accent-orange-500" />
              Solo <span className="text-orange-400 font-medium">{operazioneAttiva.nome}</span>
            </label>
          </div>
        )}
      </FilterBar>
      <div className="text-xs text-slate-500 mb-3">
        {total > 0 ? `${(page-1)*PAGE_SIZE + 1}–${Math.min(page*PAGE_SIZE, total)} di ${total} righe` : '0 righe'}
      </div>

      {loading ? <div className="text-slate-400 text-sm">Caricamento...</div> : (
        <div className="tactical-card overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-900 z-10">
              <tr className="border-b border-slate-700">
                <th className="text-left p-2 text-slate-400">Data/Ora</th>
                <th className="text-left p-2 text-slate-400">Tipo</th>
                <th className="text-left p-2 text-slate-400">Operatore</th>
                <th className="text-left p-2 text-slate-400">IP</th>
                <th className="text-left p-2 text-slate-400">Operazione</th>
                <th className="text-left p-2 text-slate-400">Entità</th>
                <th className="text-left p-2 text-slate-400">Descrizione</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {/* La key va sul FRAGMENT, che è l'elemento della lista: messa sul
                  <tr> interno React non la vede e riusa le righe per posizione —
                  con righe espandibili significa mostrare il dettaglio di un log
                  sotto un altro, appena l'elenco cambia ordine o si filtra. */}
              {filtered.map((l, i) => (
                <Fragment key={l.id || i}>
                  <tr className="border-b border-slate-800 hover:bg-slate-800/40">
                    <td className="p-2 font-mono text-slate-500 whitespace-nowrap">
                      {(() => { const ts = fmtLogTs(l.timestamp); return `${ts.data} ${ts.ora}`; })()}
                    </td>
                    <td className={`p-2 font-mono font-bold ${tipoColor(l.tipo)}`}>{l.tipo}</td>
                    <td className="p-2 text-slate-300">{l.operatore_email || <span className="text-slate-600">Sistema</span>}</td>
                    <td className="p-2 font-mono text-slate-400">{l.ip_address || <span className="text-slate-600">—</span>}</td>
                    <td className="p-2 text-orange-300/90">{l.operazione_nome || <span className="text-slate-600">—</span>}</td>
                    <td className="p-2 text-slate-400">{l.entita_tipo}</td>
                    <td className="p-2 text-slate-300">{l.descrizione}</td>
                    <td className="p-2">
                      {l.dettagli && (
                        <button className="text-slate-500 hover:text-white" onClick={() => setExpanded(expanded === i ? null : i)}>
                          {expanded === i ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === i && l.dettagli && (
                    <tr className="border-b border-slate-800 bg-slate-900">
                      <td colSpan={8} className="p-3">
                        <pre className="text-xs text-slate-400 font-mono whitespace-pre-wrap">{JSON.stringify(l.dettagli, null, 2)}</pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500">Nessun log trovato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginazione */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-slate-400">
            Pagina <span className="text-white font-medium">{page}</span> di <span className="text-white font-medium">{pages}</span>
          </span>
          <div className="flex items-center gap-1">
            <button className="pag-btn" onClick={() => setPage(1)} disabled={page === 1} title="Prima pagina">«</button>
            <button className="pag-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} title="Precedente">‹</button>
            <span className="pag-btn active">{page}</span>
            <button className="pag-btn" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} title="Successiva">›</button>
            <button className="pag-btn" onClick={() => setPage(pages)} disabled={page === pages} title="Ultima pagina">»</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Validazione POA Tab (Fase 2 - reportistica ritrovamenti storici) ─────────
const ValidazionePoaTab = ({ api }) => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroAnno, setFiltroAnno] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroEsito, setFiltroEsito] = useState("");
  const [filtroPoa, setFiltroPoa] = useState("");
  // escludi (default) | includi | solo — le esercitazioni sono fuori dal campione statistico
  const [filtroEserc, setFiltroEserc] = useState("escludi");
  const [dettaglio, setDettaglio] = useState(null);  // ritrovamento aperto in modale
  const [calib, setCalib] = useState(null);          // stato raccolta casi per calibrazione locale

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const statsParams = { esercitazioni: filtroEserc };
      if (filtroAnno) statsParams.anno = filtroAnno;
      const itemsParams = { esercitazioni: filtroEserc };
      if (filtroAnno) itemsParams.anno = filtroAnno;
      if (filtroCategoria) itemsParams.profilo_categoria = filtroCategoria;
      if (filtroEsito) itemsParams.esito = filtroEsito;
      if (filtroPoa !== "") itemsParams.poa_calcolato = filtroPoa;
      // Con POA disabilitato stats e lista sono 404 (gated dal middleware per
      // path), mentre il contatore di calibrazione resta raggiungibile: la
      // raccolta dei casi prosegue comunque e deve poter essere monitorata.
      // Quindi ogni chiamata tollera il proprio fallimento invece di far
      // fallire tutto il caricamento.
      const [statsRes, listRes, calibRes] = await Promise.all([
        api.getRitrovamentiStats(statsParams).catch(() => null),
        api.getRitrovamenti(itemsParams).catch(() => null),
        api.getStatoCalibrazione().catch(() => null),
      ]);
      setStats(statsRes?.data || null);
      setItems(listRes?.data?.items || []);
      setCalib(calibRes?.data || null);
      if (!statsRes && !listRes && !calibRes) {
        toast.error("Errore caricamento ritrovamenti");
      }
    } catch (err) {
      toast.error(apiErr(err, "Errore caricamento ritrovamenti"));
    } finally {
      setLoading(false);
    }
  }, [api, filtroAnno, filtroCategoria, filtroEsito, filtroPoa, filtroEserc]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleOpenDettaglio = async (id) => {
    try {
      const { data } = await api.getRitrovamento(id);
      setDettaglio(data);
    } catch (err) {
      toast.error("Errore caricamento dettaglio");
    }
  };

  // Palette POA UI (coerente con KML/POAPage)
  const PRIO_HEX = { ALTA: "#FF00FF", MEDIA: "#8A2BE2", BASSA: "#0000FF" };

  // Reset filtri
  const resetFiltri = () => {
    setFiltroAnno(""); setFiltroCategoria(""); setFiltroEsito(""); setFiltroPoa("");
    setFiltroEserc("escludi");
  };

  if (loading && !stats) return <div className="p-8 text-center text-slate-400">Caricamento…</div>;

  const annoCorrente = new Date().getFullYear();

  return (
    <div data-testid="validazione-poa-tab">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Target size={22} className="text-orange-400" /> {POA_ENABLED ? "Validazione POA" : "Casi POA raccolti"}
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Reportistica statistica sui ritrovamenti storici. I dati non influenzano il calcolo POA
            (basato su ISRID Koester) - solo monitoraggio modello e analisi annuali.
          </p>
        </div>
        <button className="btn btn-secondary" onClick={loadData} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
        </button>
      </div>

      {/* POA spento: la raccolta dei casi prosegue, ma le metriche di validazione
          (hit-rate) non esistono perché nessuno calcola il POA. Lo diciamo, invece
          di mostrare una pagina mezza vuota senza spiegazione. */}
      {!POA_ENABLED && (
        <div className="tactical-card p-3 mb-4 border border-amber-800/50 bg-amber-950/20">
          <p className="text-xs text-amber-300">
            <strong>POA disattivato.</strong> La raccolta dei casi continua comunque: ogni operazione
            chiusa con esito valorizzato viene archiviata nel campione statistico, e il contatore qui
            sotto la monitora. Le metriche di validazione del modello (hit-rate, distanze, esiti)
            compariranno quando il POA verrà attivato — richiedono un POA calcolato sulle zone.
          </p>
        </div>
      )}

      {/* Raccolta casi reali per la futura calibrazione locale del POA.
          Solo soglia PER CATEGORIA: il POA si calcola per profilo ISRID, quindi
          sarà una singola categoria a poter usare i dati locali quando ha
          campione sufficiente. Una barra sul totale non aggiungeva informazione e
          si leggeva in contrasto con le righe per categoria (rimossa 2026-07-30).
          Nessuno switch ISRID/locale: vedi ADR 2026-07-30-poa-isrid-vs-dati-locali. */}
      {calib && (
        <div className="tactical-card p-4 mb-4">
          <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
            <div>
              <h3 className="text-sm font-bold text-white">Casi reali raccolti per categoria ISRID</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Fonte del calcolo POA oggi: <span className="font-semibold text-orange-400">{calib.fonte_poa_attuale}</span>.
                {" "}Una categoria potrà pesare accanto a ISRID al raggiungimento di{" "}
                <span className="font-semibold text-white">{calib.soglia_categoria}</span> casi,
                indipendentemente dalle altre. Esercitazioni escluse
                {calib.casi_esercitazione_esclusi > 0 && <> ({calib.casi_esercitazione_esclusi})</>}.
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-white leading-none">{calib.casi_reali_totali}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">casi in archivio</div>
              {calib.categorie_pronte > 0 && (
                <div className="text-[10px] text-emerald-400 mt-0.5">{calib.categorie_pronte} categorie pronte</div>
              )}
            </div>
          </div>
          {calib.per_categoria?.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 text-left">
                    <th className="py-1 pr-3 font-medium">Categoria ISRID</th>
                    <th className="py-1 pr-3 font-medium">Casi</th>
                    <th className="py-1 pr-3 font-medium" style={{ width: '35%' }}>Avanzamento verso {calib.soglia_categoria}</th>
                    <th className="py-1 pr-3 font-medium">Con POA</th>
                    <th className="py-1 pr-3 font-medium">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {calib.per_categoria.map(c => (
                    <tr key={c.categoria_isrid} className="border-t border-slate-800">
                      <td className="py-1 pr-3 text-slate-300">{c.nome_it || c.categoria_isrid}</td>
                      <td className="py-1 pr-3 font-mono text-white">
                        {c.casi}<span className="text-slate-600"> / {calib.soglia_categoria}</span>
                      </td>
                      <td className="py-1 pr-3">
                        {/* La barra è per categoria: è la soglia che abilita davvero
                            l'uso dei dati locali per quel profilo. */}
                        <div className="h-1.5 rounded bg-slate-800 overflow-hidden">
                          <div className={`h-full transition-all ${c.pronta ? 'bg-emerald-500' : 'bg-orange-500'}`}
                            style={{ width: `${Math.min(100, Math.round(100 * (c.casi || 0) / (calib.soglia_categoria || 1)))}%` }} />
                        </div>
                      </td>
                      <td className="py-1 pr-3 font-mono text-slate-400">{c.casi_con_poa}</td>
                      <td className="py-1 pr-3">
                        {c.pronta
                          ? <span className="badge badge-green text-[10px]">SUFFICIENTE</span>
                          : <span className="text-slate-500">mancano {c.mancanti}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Filtri — nascosti a POA spento: governano solo stats e lista, che in
          quel caso non vengono restituite dal backend (endpoint gated). */}
      {POA_ENABLED && (
      <div className="mb-4 flex flex-wrap items-center gap-2 p-2 bg-slate-900/40 rounded border border-slate-800">
        <label className="text-xs text-slate-400">Anno:</label>
        <select className="form-select text-xs py-1 px-2"
          value={filtroAnno} onChange={(e) => setFiltroAnno(e.target.value)}>
          <option value="">Tutti</option>
          {[annoCorrente, annoCorrente - 1, annoCorrente - 2, annoCorrente - 3].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <label className="text-xs text-slate-400 ml-2">Categoria:</label>
        <select className="form-select text-xs py-1 px-2"
          value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
          <option value="">Tutte</option>
          {["child", "youth", "hiker", "sport", "climber", "outdoor", "winter",
            "vehicle", "water", "hunter", "worker", "medical", "other"].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <label className="text-xs text-slate-400 ml-2">Esito:</label>
        <select className="form-select text-xs py-1 px-2"
          value={filtroEsito} onChange={(e) => setFiltroEsito(e.target.value)}>
          <option value="">Tutti</option>
          {["POSITIVO", "TROVATO_VIVO", "TROVATO_MORTO", "NON_TROVATO", "RECUPERATO", "SOSPESO", "ALTRO"].map(e => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
        <label className="text-xs text-slate-400 ml-2">Esercitazioni:</label>
        <select className="form-select text-xs py-1 px-2"
          value={filtroEserc} onChange={(e) => setFiltroEserc(e.target.value)}
          title="Di default le esercitazioni sono escluse: il campione statistico deve essere di soli casi reali"
          data-testid="filtro-esercitazioni">
          <option value="escludi">Escluse</option>
          <option value="includi">Incluse</option>
          <option value="solo">Solo esercitazioni</option>
        </select>
        <label className="text-xs text-slate-400 ml-2">POA:</label>
        <select className="form-select text-xs py-1 px-2"
          value={filtroPoa} onChange={(e) => setFiltroPoa(e.target.value)}>
          <option value="">Tutti</option>
          <option value="true">Solo con POA</option>
          <option value="false">Solo senza POA</option>
        </select>
        <button className="btn btn-secondary text-xs ml-2" onClick={resetFiltri}>
          <X size={12} /> Azzera filtri
        </button>
      </div>
      )}

      {/* Caso zero */}
      {stats && stats.totale_casi === 0 && (
        <div className="tactical-card p-8 text-center border border-slate-700">
          <AlertTriangle size={32} className="mx-auto mb-3 text-slate-600" />
          <p className="text-slate-400 mb-1">Nessun ritrovamento storico registrato {filtroAnno && `per il ${filtroAnno}`}.</p>
          <p className="text-slate-500 text-xs">
            Gli snapshot vengono generati automaticamente al salvataggio della Scheda 3 di chiusura
            quando il campo 'esito' è valorizzato.
          </p>
        </div>
      )}

      {/* Dashboard cards */}
      {stats && stats.totale_casi > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Casi totali" value={stats.totale_casi} color="orange" />
            <StatCard label="Con POA calcolato" value={stats.casi_con_poa_calcolato} color="blue" />
            <StatCard label="Hit top-1" value={`${stats.hit_rate_pct.top_1}%`}
              color={stats.hit_rate_pct.top_1 >= 50 ? "green" : stats.hit_rate_pct.top_1 >= 25 ? "orange" : "red"} />
            <StatCard label="Hit top-3" value={`${stats.hit_rate_pct.top_3}%`}
              color={stats.hit_rate_pct.top_3 >= 70 ? "green" : "orange"} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {/* Distanza */}
            <div className="tactical-card p-3 border border-slate-700">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Distanza PUA → ritrovamento (km)</div>
              <div className="flex gap-3 text-sm">
                <div><div className="text-slate-500 text-[10px]">Media</div><div className="font-mono text-white">{stats.distanza_km.media ?? '–'}</div></div>
                <div><div className="text-slate-500 text-[10px]">Mediana</div><div className="font-mono text-white">{stats.distanza_km.mediana ?? '–'}</div></div>
                <div><div className="text-slate-500 text-[10px]">Min</div><div className="font-mono text-white">{stats.distanza_km.min ?? '–'}</div></div>
                <div><div className="text-slate-500 text-[10px]">Max</div><div className="font-mono text-white">{stats.distanza_km.max ?? '–'}</div></div>
              </div>
            </div>
            {/* Tempo */}
            <div className="tactical-card p-3 border border-slate-700">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Tempo scomparsa → ritrovamento (h)</div>
              <div className="flex gap-3 text-sm">
                <div><div className="text-slate-500 text-[10px]">Media</div><div className="font-mono text-white">{stats.tempo_h.media ?? '–'}</div></div>
                <div><div className="text-slate-500 text-[10px]">Mediana</div><div className="font-mono text-white">{stats.tempo_h.mediana ?? '–'}</div></div>
                <div><div className="text-slate-500 text-[10px]">Min</div><div className="font-mono text-white">{stats.tempo_h.min ?? '–'}</div></div>
                <div><div className="text-slate-500 text-[10px]">Max</div><div className="font-mono text-white">{stats.tempo_h.max ?? '–'}</div></div>
              </div>
            </div>
          </div>

          {/* Esiti + Luoghi */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="tactical-card p-3 border border-slate-700">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Distribuzione esiti</div>
              {Object.keys(stats.esiti).length === 0
                ? <div className="text-slate-500 text-sm">—</div>
                : Object.entries(stats.esiti).sort(([, a], [, b]) => b - a).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 text-sm">
                      <span className="font-mono text-slate-300 w-32 truncate">{k}</span>
                      <div className="flex-1 h-2 bg-slate-800 rounded overflow-hidden">
                        <div className="h-full bg-orange-500" style={{ width: `${(v / stats.totale_casi) * 100}%` }} />
                      </div>
                      <span className="font-mono text-slate-200 text-xs w-10 text-right">{v}</span>
                    </div>
                  ))
              }
            </div>
            <div className="tactical-card p-3 border border-slate-700">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Luoghi di ritrovamento</div>
              {Object.keys(stats.luoghi_ritrovamento).length === 0
                ? <div className="text-slate-500 text-sm">—</div>
                : Object.entries(stats.luoghi_ritrovamento).sort(([, a], [, b]) => b - a).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 text-sm">
                      <span className="font-mono text-slate-300 w-28 truncate">{k}</span>
                      <div className="flex-1 h-2 bg-slate-800 rounded overflow-hidden">
                        <div className="h-full bg-cyan-600" style={{ width: `${(v / stats.totale_casi) * 100}%` }} />
                      </div>
                      <span className="font-mono text-slate-200 text-xs w-10 text-right">{v}</span>
                    </div>
                  ))
              }
            </div>
          </div>

          {/* Per categoria */}
          {stats.per_categoria && stats.per_categoria.length > 0 && (
            <div className="tactical-card p-3 border border-slate-700 mb-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">Statistiche per categoria profilo</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-400 uppercase">
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2">Categoria</th>
                      <th className="text-right">Casi</th>
                      <th className="text-right">Dist. media</th>
                      <th className="text-right">P50 ISRID</th>
                      <th className="text-right">Δ vs ISRID</th>
                      <th className="text-right">Hit top-1</th>
                      <th className="text-right">Hit top-3</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-xs">
                    {stats.per_categoria.map(c => (
                      <tr key={c.categoria} className="border-b border-slate-800 hover:bg-slate-900/40">
                        <td className="py-2 text-orange-400 font-bold">{c.categoria}</td>
                        <td className="text-right text-slate-200">{c.casi}</td>
                        <td className="text-right text-slate-200">{c.distanza_media_km ?? '–'} km</td>
                        <td className="text-right text-slate-400">{c.p50_isrid_km ?? '–'} km</td>
                        <td className={`text-right ${c.delta_vs_isrid_pct == null ? 'text-slate-500' : c.delta_vs_isrid_pct < -30 ? 'text-red-400' : c.delta_vs_isrid_pct > 30 ? 'text-amber-400' : 'text-green-400'}`}>
                          {c.delta_vs_isrid_pct == null ? '–' : `${c.delta_vs_isrid_pct > 0 ? '+' : ''}${c.delta_vs_isrid_pct}%`}
                        </td>
                        <td className="text-right text-slate-200">{c.hit_top_1}/{c.casi_con_poa} ({c.hit_rate_top_1_pct}%)</td>
                        <td className="text-right text-slate-200">{c.hit_top_3}/{c.casi_con_poa} ({c.hit_rate_top_3_pct}%)</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Lista ritrovamenti */}
      {items && items.length > 0 && (
        <div className="tactical-card p-3 border border-slate-700">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">
            Elenco ritrovamenti ({items.length} mostrati)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-400 uppercase">
                <tr className="border-b border-slate-700">
                  <th className="text-left py-2">Data</th>
                  <th className="text-left">Categoria</th>
                  <th className="text-left">Esito</th>
                  <th className="text-left">Luogo</th>
                  <th className="text-right">Dist. km</th>
                  <th className="text-right">Tempo h</th>
                  <th className="text-right">Top zona</th>
                  <th className="text-right">Hit</th>
                  <th className="text-center">Azioni</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                {items.map(r => (
                  <tr key={r.id} className="border-b border-slate-800 hover:bg-slate-900/40">
                    <td className="py-2 text-slate-300">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString('it-IT') : '–'}
                      {r.esercitazione && (
                        <span className="badge badge-yellow text-[9px] ml-1" title="Esercitazione: fuori dal campione statistico">ESERC.</span>
                      )}
                      {r.anonimizzato_at && (
                        <span className="badge badge-gray text-[9px] ml-1" title="Operazione eliminata: snapshot conservato in forma anonimizzata">ANON.</span>
                      )}
                    </td>
                    <td className="text-slate-200">{r.profilo_categoria || '–'} <span className="text-slate-500">({r.categoria_isrid || '–'})</span></td>
                    <td className="text-slate-200">{r.esito || '–'}</td>
                    <td className="text-slate-300">{r.luogo_ritrovamento || '–'}</td>
                    <td className="text-right text-slate-200">{r.distanza_pua_km ?? '–'}</td>
                    <td className="text-right text-slate-200">{r.tempo_elapsed_h ?? '–'}</td>
                    <td className="text-right text-slate-300">{r.top_zona_nome ? `${r.top_zona_nome} (${(r.top_zona_poa * 100).toFixed(0)}%)` : '–'}</td>
                    <td className="text-right">
                      {r.hit_first_choice
                        ? <span className="text-green-400">✓ #1</span>
                        : r.hit_top_3
                          ? <span className="text-amber-400">✓ #{r.zona_ritrovamento_rank}</span>
                          : <span className="text-rose-400">✗</span>}
                    </td>
                    <td className="text-center">
                      <button className="text-orange-400 hover:text-orange-300 text-xs"
                        onClick={() => handleOpenDettaglio(r.id)}>
                        Dettaglio
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modale dettaglio */}
      {dettaglio && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDettaglio(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Dettaglio ritrovamento</h3>
              <button onClick={() => setDettaglio(null)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500">Operazione ID:</span> <span className="font-mono text-slate-300">{dettaglio.operazione_id}</span></div>
                <div><span className="text-slate-500">Chiusa da:</span> <span className="text-slate-300">{dettaglio.operatore_chiusura || '–'}</span></div>
                <div><span className="text-slate-500">Categoria:</span> <span className="text-orange-400">{dettaglio.profilo_categoria}</span> ({dettaglio.categoria_isrid})</div>
                <div><span className="text-slate-500">Esito:</span> <span className="text-slate-200">{dettaglio.esito}</span></div>
                <div><span className="text-slate-500">Luogo:</span> <span className="text-slate-200">{dettaglio.luogo_ritrovamento}</span> ({dettaglio.terrain_type_reale})</div>
                <div><span className="text-slate-500">Meteo:</span> <span className="text-slate-200">{dettaglio.tempo_meteo_iniziale || '–'}</span></div>
                <div><span className="text-slate-500">PUA:</span> <span className="font-mono text-slate-300">{dettaglio.pua_lat?.toFixed(5)}, {dettaglio.pua_lon?.toFixed(5)}</span></div>
                <div><span className="text-slate-500">Ritrovamento:</span> <span className="font-mono text-slate-300">{dettaglio.ritrovamento_lat?.toFixed(5)}, {dettaglio.ritrovamento_lon?.toFixed(5)}</span></div>
                <div><span className="text-slate-500">Distanza:</span> <span className="font-mono text-white">{dettaglio.distanza_pua_km} km</span></div>
                <div><span className="text-slate-500">Tempo trascorso:</span> <span className="font-mono text-white">{dettaglio.tempo_elapsed_h} h</span></div>
                <div><span className="text-slate-500">P50 ISRID atteso:</span> <span className="font-mono text-slate-400">{dettaglio.profilo_distanza_p50_km} km</span></div>
                <div><span className="text-slate-500">Area ricerca:</span> <span className="font-mono text-slate-300">{dettaglio.area_ricerca_km2?.toFixed(3) || '–'} km²</span></div>
              </div>
              <div className="pt-3 border-t border-slate-800">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Validazione POA</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-slate-500">Zone totali:</span> <span className="text-slate-200">{dettaglio.num_zone_totali}</span></div>
                  <div><span className="text-slate-500">Top zona prevista:</span> <span className="text-orange-400">{dettaglio.top_zona_nome} ({(dettaglio.top_zona_poa * 100).toFixed(1)}%)</span></div>
                  <div><span className="text-slate-500">Zona ritrovamento:</span> <span className="text-slate-200">{dettaglio.zona_ritrovamento_nome || <span className="text-rose-400 italic">fuori zona</span>}</span></div>
                  <div><span className="text-slate-500">Rank zona:</span> <span className="text-slate-200">{dettaglio.zona_ritrovamento_rank ?? '–'}</span></div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Hit:</span>{' '}
                    <span className={dettaglio.hit_first_choice ? 'text-green-400' : 'text-rose-400'}>top-1 {dettaglio.hit_first_choice ? '✓' : '✗'}</span>{' · '}
                    <span className={dettaglio.hit_top_3 ? 'text-green-400' : 'text-rose-400'}>top-3 {dettaglio.hit_top_3 ? '✓' : '✗'}</span>{' · '}
                    <span className={dettaglio.hit_top_5 ? 'text-green-400' : 'text-rose-400'}>top-5 {dettaglio.hit_top_5 ? '✓' : '✗'}</span>
                  </div>
                </div>
              </div>
              {dettaglio.snapshot_zone && Array.isArray(dettaglio.snapshot_zone) && dettaglio.snapshot_zone.length > 0 && (
                <div className="pt-3 border-t border-slate-800">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Snapshot zone (al momento chiusura)</div>
                  <table className="w-full text-xs font-mono">
                    <thead className="text-slate-400">
                      <tr><th className="text-left">#</th><th className="text-left">Nome</th><th className="text-left">Tipo</th><th className="text-right">POA</th><th className="text-left">Stato</th></tr>
                    </thead>
                    <tbody>
                      {dettaglio.snapshot_zone
                        .slice()
                        .sort((a, b) => (a.rank || 999) - (b.rank || 999))
                        .map(z => (
                        <tr key={z.id} className={`border-t border-slate-800 ${z.id === dettaglio.zona_ritrovamento_id ? 'bg-orange-950/30' : ''}`}>
                          <td className="text-slate-500">{z.rank ?? '–'}</td>
                          <td className="text-orange-400">{z.nome}</td>
                          <td className="text-slate-400">{z.tipo}</td>
                          <td className="text-right text-slate-200">{z.poa != null ? `${(z.poa * 100).toFixed(1)}%` : '–'}</td>
                          <td className="text-slate-400">{z.stato}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ── Impostazioni Tab ──────────────────────────────────────────────────────────
const ImpostazioniTab = ({ api }) => {
  const [form, setForm] = useState({ nome_sistema: "TAS Command", colore_primario: "#f97316", colore_secondario: "#1e293b", logo_base64: null, favicon_base64: null });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.getImpostazioni(); setForm(f => ({ ...f, ...data })); }
    catch { toast.error("Errore caricamento impostazioni"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("File troppo grande (max 500KB)"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, [field]: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveImpostazioni(form);
      applyTheme(form);
      window.dispatchEvent(new CustomEvent('tas-theme-update', { detail: form }));
      toast.success("Impostazioni salvate e tema applicato!");
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    }
    catch (e) { toast.error(apiErr(e, "Errore nel salvataggio")); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-400 text-sm">Caricamento...</div>;

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-bold text-white mb-6">Impostazioni Grafiche</h2>

      <div className="tactical-card p-6 space-y-6">
        <div className="form-group">
          <label className="form-label">Nome Sistema</label>
          <input className="form-input" value={form.nome_sistema || ""} onChange={e => setForm(f => ({ ...f, nome_sistema: e.target.value }))} placeholder="TAS Command" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="form-group">
            <label className="form-label flex items-center gap-2"><Palette size={14} /> Colore Primario</label>
            <div className="flex gap-2 items-center">
              <input type="color" className="w-10 h-10 rounded cursor-pointer bg-transparent border-0" value={form.colore_primario || "#f97316"}
                onChange={e => { const v = e.target.value; setForm(f => ({ ...f, colore_primario: v })); applyTheme({ colore_primario: v }); }} />
              <input className="form-input flex-1 font-mono text-sm" value={form.colore_primario || ""}
                onChange={e => { const v = e.target.value; setForm(f => ({ ...f, colore_primario: v })); if (/^#[0-9a-fA-F]{6}$/.test(v)) applyTheme({ colore_primario: v }); }} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label flex items-center gap-2"><Palette size={14} /> Colore Secondario</label>
            <div className="flex gap-2 items-center">
              <input type="color" className="w-10 h-10 rounded cursor-pointer bg-transparent border-0" value={form.colore_secondario || "#1e293b"}
                onChange={e => { const v = e.target.value; setForm(f => ({ ...f, colore_secondario: v })); applyTheme({ colore_secondario: v }); }} />
              <input className="form-input flex-1 font-mono text-sm" value={form.colore_secondario || ""}
                onChange={e => { const v = e.target.value; setForm(f => ({ ...f, colore_secondario: v })); if (/^#[0-9a-fA-F]{6}$/.test(v)) applyTheme({ colore_secondario: v }); }} />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Logo (max 500KB — PNG/SVG consigliato)</label>
          <input type="file" accept="image/*" className="form-input text-sm" onChange={e => handleFileChange(e, "logo_base64")} />
          {form.logo_base64 && (
            <div className="mt-2 p-3 bg-slate-800 rounded-lg inline-block">
              <img src={form.logo_base64} alt="Logo preview" className="h-12 object-contain" />
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Favicon (max 500KB — ICO/PNG 32×32)</label>
          <input type="file" accept="image/*" className="form-input text-sm" onChange={e => handleFileChange(e, "favicon_base64")} />
          {form.favicon_base64 && (
            <div className="mt-2 p-3 bg-slate-800 rounded-lg inline-block">
              <img src={form.favicon_base64} alt="Favicon preview" className="h-8 object-contain" />
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-slate-700">
          <div className="text-xs text-slate-500 mb-4">
            I colori vengono applicati in tempo reale durante la modifica e salvati permanentemente nel database. Il tema viene caricato automaticamente ad ogni avvio dell'app.
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Salvataggio..." : "Salva Impostazioni"}
            </button>
            {saved && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-500/15 border border-green-500/40 rounded-lg text-green-400 text-sm font-medium animate-pulse">
                <CheckCircle size={15} />
                Impostazioni salvate correttamente
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Tas2PersonaleTab ──────────────────────────────────────────────────────────
const EMPTY_FORM = { cognome: "", nome: "", qualifica: "", regione: "", comando: "", sede: "", turno: "", salto: "", telefono: "", email: "", tas2_dal: "", attivo: true };
// Stessi valori del profilo personale (ProfiloPage) e della validazione backend.
const TURNI_TAS2 = ["A", "B", "C", "D", "AC", "BD", "GG"];
const SALTI_TAS2 = [1, 2, 3, 4, 5, 6, 7, 8];

const Tas2PersonaleTab = ({ api }) => {
  const [records, setRecords]   = useState([]);
  const [search, setSearch]     = useState("");
  const [regione, setRegione]   = useState("");
  const [comando, setComando]   = useState("");
  const [filtroTurno, setFiltroTurno] = useState("");
  const [filtroStato, setFiltroStato] = useState("");
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(null); // null | { mode: "create" | "edit", data: obj }
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  // Import massivo da Excel
  const importInputRef          = useRef(null);
  const [importPreview, setImportPreview] = useState(null); // { valid:[], errors:[] } | null
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (comando)        params.comando = comando;
      const { data } = await api.getTas2Personale(params);
      setRecords(data);
    } catch { toast.error("Errore caricamento personale TAS2"); }
    finally { setLoading(false); }
  }, [api, search, comando]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...EMPTY_FORM }); setModal({ mode: "create" }); };

  /** Abbina una sede abbreviata (es. "Castellammare d.S.") alla voce esatta
   *  in vvfData usando word-prefix matching:
   *  ogni parola dell'abbreviazione deve essere prefisso della parola corrispondente
   *  nell'opzione estesa ("d" → "di", "S" → "Stabia", "L" → "Lucania" …). */
  const matchSede = (stored, opzioni) => {
    if (!stored) return "";
    if (opzioni.includes(stored)) return stored;
    const exactCI = opzioni.find(o => o.toLowerCase() === stored.toLowerCase());
    if (exactCI) return exactCI;
    const sub = opzioni.find(o => o.toLowerCase().includes(stored.toLowerCase()));
    if (sub) return sub;
    const norm = s => s.replace(/\./g, "").toLowerCase().split(/\s+/).filter(Boolean);
    const sw = norm(stored);
    const prefix = opzioni.find(opt => {
      const ow = norm(opt);
      return ow.length >= sw.length && sw.every((w, i) => ow[i] && ow[i].startsWith(w));
    });
    return prefix || stored; // fallback: mantieni valore originale (mostrato via option extra)
  };

  const openEdit   = (r)  => {
    // Normalizza regione: match case-insensitive su VVF_REGIONI
    let regioneVal = r.regione || "";
    if (regioneVal) {
      const match = VVF_REGIONI.find(reg => reg.toLowerCase() === regioneVal.toLowerCase());
      if (match) regioneVal = match;
    }
    // Auto-rileva regione da comando se ancora vuota
    const comandoVal = (r.comando || "").toUpperCase();
    if (!regioneVal && comandoVal) {
      const found = Object.entries(VVF_DATA).find(([, cmds]) =>
        Object.keys(cmds).includes(comandoVal)
      );
      if (found) regioneVal = found[0];
    }
    // Normalizza sede con word-prefix matching (gestisce abbreviazioni tipo "Vallo d.L.")
    const sedeVal = matchSede(r.sede || "", getSedi(regioneVal, comandoVal));
    setForm({
      cognome: fmtCognome(r.cognome), nome: r.nome || "", qualifica: r.qualifica || "",
      regione: regioneVal, comando: comandoVal, sede: sedeVal,
      turno: r.turno || "", salto: r.salto ?? "", telefono: r.telefono || "",
      email: (r.email || "").toLowerCase(), tas2_dal: r.tas2_dal ?? "", attivo: r.attivo !== false,
    });
    setModal({ mode: "edit", id: r.id });
  };
  const closeModal = () => { setModal(null); setSaving(false); };

  const handleSave = async () => {
    if (!form.cognome.trim() || !form.nome.trim()) {
      toast.error("Cognome e Nome sono obbligatori"); return;
    }
    setSaving(true);
    const payload = {
      ...form,
      cognome:  fmtCognome(form.cognome).trim(),
      email:    form.email.trim().toLowerCase(),
      salto:    form.salto    !== "" ? Number(form.salto)    : null,
      tas2_dal: form.tas2_dal !== "" ? Number(form.tas2_dal) : null,
    };
    try {
      if (modal.mode === "create") {
        await api.createTas2(payload);
        toast.success("Personale TAS2 aggiunto");
      } else {
        await api.updateTas2(modal.id, payload);
        toast.success("Personale TAS2 aggiornato");
      }
      closeModal();
      load();
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, nome) => {
    if (!(await confirmDialog({ title: "Elimina dalla whitelist", message: `Eliminare "${nome}" dalla whitelist TAS2?`, danger: true, confirmLabel: "Elimina" }))) return;
    try { await api.deleteTas2(id); toast.success("Record eliminato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };


  const bulkDelete = async () => {
    const n = sel.count;
    if (!(await confirmDialog({
      title: "Elimina dalla whitelist",
      message: `Eliminare ${n} record dalla whitelist TAS2? Chi non è in whitelist non può registrarsi.`,
      danger: true, confirmLabel: "Elimina",
    }))) return;
    const { ok, ko } = await eseguiAzioneMassiva(sel.ids, (id) => api.deleteTas2(id));
    if (ok) toast.success(`${ok} eliminat${ok === 1 ? "o" : "i"}`);
    if (ko) toast.error(`${ko} non eliminat${ko === 1 ? "o" : "i"}`);
    sel.pulisci(); load();
  };

  // ── Import massivo da Excel ──────────────────────────────────────────────
  // "Cognome Nome" → cognome (parole iniziali maiuscole) + nome (il resto).
  const splitCognomeNome = (raw) => {
    const parts = String(raw || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { cognome: "", nome: "" };
    if (parts.length === 1) return { cognome: parts[0], nome: "" };
    let i = 0;
    while (i < parts.length - 1 && parts[i] === parts[i].toUpperCase() && /[A-ZÀ-Ý]/.test(parts[i])) i++;
    if (i === 0) i = 1; // almeno una parola di cognome
    return { cognome: parts.slice(0, i).join(" "), nome: parts.slice(i).join(" ") };
  };
  // "Comando / Sede" → comando + sede (separatore "/").
  const splitComandoSede = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return { comando: "", sede: "" };
    const idx = s.indexOf("/");
    if (idx === -1) return { comando: s, sede: "" };
    return { comando: s.slice(0, idx).trim(), sede: s.slice(idx + 1).trim() };
  };

  const downloadImportTemplate = async () => {
    const XLSX = await loadXLSX();
    // Una colonna per ogni campo della maschera "Aggiungi Personale TAS2", nello
    // stesso ordine: l'import le legge per nome, quindi corrispondono 1:1.
    const ws = XLSX.utils.aoa_to_sheet([
      ["Cognome", "Nome", "Qualifica", "Regione", "Comando", "Sede", "Turno", "Salto", "Telefono", "Email", "TAS2 dal", "Attivo"],
      ["ROSSI", "Mario", "VE", "Campania", "NAPOLI", "Sede centrale", "A", 1, "3331234567", "mario.rossi@vigilfuoco.it", 2019, "SI"],
      ["DE LUCA", "Anna", "CS", "Campania", "SALERNO", "Sede centrale", "BD", 5, "3479876543", "anna.deluca@vigilfuoco.it", 2021, "SI"],
    ]);
    ws["!cols"] = [{ wch: 18 }, { wch: 16 }, { wch: 10 }, { wch: 16 }, { wch: 20 }, { wch: 22 }, { wch: 8 }, { wch: 8 }, { wch: 16 }, { wch: 30 }, { wch: 10 }, { wch: 8 }];
    const guida = XLSX.utils.aoa_to_sheet([
      ["Campo", "Valori ammessi"],
      ["Cognome", "Obbligatorio. Viene convertito in MAIUSCOLO"],
      ["Nome", "Obbligatorio"],
      ["Regione / Comando / Sede", "Come negli elenchi a discesa della maschera (es. Campania / NAPOLI / Sede centrale)"],
      ["Turno", TURNI_TAS2.join(", ")],
      ["Salto", SALTI_TAS2.join(", ")],
      ["Email", "Viene convertita in minuscolo"],
      ["TAS2 dal", "Anno a 4 cifre (es. 2019)"],
      ["Attivo", "SI oppure NO (vuoto = SI)"],
    ]);
    guida["!cols"] = [{ wch: 26 }, { wch: 80 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TAS2");
    XLSX.utils.book_append_sheet(wb, guida, "Istruzioni");
    XLSX.writeFile(wb, "template_personale_TAS2.xlsx");
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // consente re-selezione dello stesso file
    if (!file) return;
    // Limite dimensione lato client (anti-DoS sul parsing).
    if (file.size > 5 * 1024 * 1024) { toast.error("File troppo grande (max 5 MB)"); return; }
    try {
      const XLSX = await loadXLSX();
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames.find(n => /tas2|personale|elenco/i.test(n)) || wb.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1 });
      // Trova intestazione (riga con "COGNOME").
      let headerIdx = rows.findIndex(r => Array.isArray(r) && r.some(c => /cognome/i.test(String(c))));
      if (headerIdx === -1) headerIdx = 0;
      const headers = (rows[headerIdx] || []).map(h => String(h || "").toUpperCase().replace(/[.\s*]/g, ""));
      // Nome colonna ESATTO (non "contiene"): "NOME" altrimenti prenderebbe "COGNOME".
      const col = (keys) => headers.findIndex(h => keys.includes(h));
      const cMap = {
        cognome:     col(["COGNOME"]),
        nome:        col(["NOME"]),
        cognomeNome: col(["COGNOMENOME"]),   // vecchio template: colonna unica
        qualifica:   col(["QUALIFICA", "QUAL"]),
        regione:     col(["REGIONE"]),
        comando:     col(["COMANDO", "COMANDO/SEDE"]),
        sede:        col(["SEDE"]),
        turno:       col(["TURNO"]),
        salto:       col(["SALTO"]),
        telefono:    col(["TELEFONO", "TEL", "CELLULARE", "CELL"]),
        email:       col(["EMAIL", "MAIL"]),
        dal:         col(["TAS2DAL", "DAL"]),
        attivo:      col(["ATTIVO"]),
      };
      if (cMap.cognome === -1 && cMap.cognomeNome === -1) { toast.error("Colonna 'Cognome' non trovata"); return; }

      const MAX = 1000;
      const get = (row, i) => (i >= 0 ? String(row[i] ?? "").trim() : "");
      const valid = [], errors = [];
      const seen = new Set();
      rows.slice(headerIdx + 1).forEach((row, n) => {
        if (!Array.isArray(row) || !row.some(c => c !== null && c !== undefined && String(c).trim() !== "")) return; // riga vuota → ignora
        if (valid.length >= MAX) return;
        const riga = headerIdx + n + 2;
        // Nuovo formato: Cognome e Nome in colonne separate. Vecchio: "Cognome Nome" unico.
        const { cognome: cognomeRaw, nome: nomeRaw } = cMap.cognome !== -1
          ? { cognome: get(row, cMap.cognome), nome: get(row, cMap.nome) }
          : splitCognomeNome(get(row, cMap.cognomeNome));
        const cognome = fmtCognome(cognomeRaw);
        const nome = nomeRaw;
        const cn = `${cognome} ${nome}`.trim();
        // Vecchio formato: "Comando / Sede" nella stessa colonna.
        const { comando: comandoRaw, sede: sedeRaw } = cMap.sede !== -1
          ? { comando: get(row, cMap.comando), sede: get(row, cMap.sede) }
          : splitComandoSede(get(row, cMap.comando));
        const comando = comandoRaw.toUpperCase();
        // Regione: abbina agli elenchi (case-insensitive); se manca la ricava dal comando.
        let regione = get(row, cMap.regione);
        const regMatch = VVF_REGIONI.find(r => r.toLowerCase() === regione.toLowerCase());
        if (regMatch) regione = regMatch;
        if (!regione && comando) {
          const found = Object.entries(VVF_DATA).find(([, cmds]) => Object.keys(cmds).includes(comando));
          if (found) regione = found[0];
        }
        const sede = matchSede(sedeRaw, getSedi(regione, comando));
        const email = get(row, cMap.email).toLowerCase();
        const turno = get(row, cMap.turno).toUpperCase();
        const saltoRaw = get(row, cMap.salto);
        const salto = saltoRaw === "" ? null : Number(saltoRaw);
        const attivoRaw = get(row, cMap.attivo).toUpperCase();
        const rec = {
          cognome, nome,
          qualifica: get(row, cMap.qualifica),
          regione,
          comando, sede,
          turno, salto,
          telefono:  get(row, cMap.telefono),
          email,
          tas2_dal:  /^\d{4}$/.test(get(row, cMap.dal)) ? Number(get(row, cMap.dal)) : null,
          attivo:    !["NO", "N", "0", "FALSE", "INATTIVO"].includes(attivoRaw),
        };
        if (!cognome) { errors.push({ riga, motivo: "Cognome mancante", cn }); return; }
        if (turno && !TURNI_TAS2.includes(turno)) { errors.push({ riga, motivo: `Turno non valido: ${turno} (ammessi ${TURNI_TAS2.join(", ")})`, cn }); return; }
        if (salto !== null && !SALTI_TAS2.includes(salto)) { errors.push({ riga, motivo: `Salto non valido: ${saltoRaw} (ammessi 1–8)`, cn }); return; }
        if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { errors.push({ riga, motivo: `Email non valida: ${email}`, cn }); return; }
        if (email && seen.has(email)) { errors.push({ riga, motivo: `Email duplicata nel file: ${email}`, cn }); return; }
        if (email) seen.add(email);
        valid.push(rec);
      });
      const truncated = rows.slice(headerIdx + 1).filter(r => Array.isArray(r) && r.some(c => String(c ?? "").trim() !== "")).length > MAX;
      setImportPreview({ valid, errors, truncated });
    } catch (err) {
      console.error(err);
      toast.error("Errore lettura file — verifica il formato Excel");
    }
  };

  const handleConfirmImport = async () => {
    if (!importPreview?.valid?.length) return;
    setImporting(true);
    try {
      const { data } = await api.importTas2({ records: importPreview.valid });
      toast.success(`Import completato: ${data.imported} aggiunti${data.skipped ? `, ${data.skipped} saltati` : ""}${data.errors?.length ? `, ${data.errors.length} errori` : ""}`);
      setImportPreview(null);
      load();
    } catch (e) {
      toast.error(apiErr(e, "Errore importazione"));
    } finally { setImporting(false); }
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const turniDisponibili = [...new Set(records.map(r => (r.turno || "").trim()).filter(Boolean))].sort();

  const recordsFiltrati = records.filter(r =>
    (!regione || (r.regione || "").toLowerCase() === regione.toLowerCase()) &&
    (!filtroTurno || (r.turno || "").trim() === filtroTurno) &&
    (!filtroStato || (filtroStato === "attivo" ? r.attivo !== false : r.attivo === false))
  );


  const sel = useSelezioneMultipla(records, recordsFiltrati);

  const bulkAttivo = async (attivo) => {
    const { ok, ko } = await eseguiAzioneMassiva(sel.ids, (id) => api.updateTas2(id, { attivo }));
    if (ok) toast.success(`${ok} record ${attivo ? "attivat" : "disattivat"}${ok === 1 ? "o" : "i"}`);
    if (ko) toast.error(`${ko} non aggiornat${ko === 1 ? "o" : "i"}`);
    sel.pulisci(); load();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-white">Whitelist Personale TAS2</h2>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /></button>
          <input ref={importInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportFile} />
          <button className="btn btn-secondary btn-sm" onClick={downloadImportTemplate} title="Scarica il template Excel"><FileDown size={14} /> Template</button>
          <button className="btn btn-secondary btn-sm" onClick={() => importInputRef.current?.click()} title="Importa personale TAS2 da Excel"><Upload size={14} /> Importa Excel</button>
          <button className="btn btn-primary btn-sm" onClick={openCreate}><Plus size={14} /> Aggiungi</button>
        </div>
      </div>
      <FilterBar onReset={() => { setSearch(""); setRegione(""); setComando(""); setFiltroTurno(""); setFiltroStato(""); }}>
        <FilterInput label="Cerca" value={search} onChange={setSearch} placeholder="Nome, email…" />
        <FilterSelect label="Regione" value={regione} onChange={v => { setRegione(v); setComando(""); }}
          options={VVF_REGIONI.filter(r => r !== "Nazionale")}
          placeholder="Tutte le regioni" />
        <FilterSelect label="Comando" value={comando} onChange={setComando}
          options={regione ? getComandi(regione).filter(c => c !== "") : VVF_REGIONI.flatMap(r => getComandi(r).filter(c => c !== ""))}
          placeholder="Tutti i comandi" />
        <FilterSelect label="Turno" value={filtroTurno} onChange={setFiltroTurno}
          options={turniDisponibili} placeholder="Tutti i turni" />
        <FilterSelect label="Stato" value={filtroStato} onChange={setFiltroStato}
          options={[{ value: "attivo", label: "Attivo" }, { value: "inattivo", label: "Inattivo" }]}
          placeholder="Tutti" />
      </FilterBar>

      {/* Totale */}
      <div className="text-xs text-slate-400 mb-3">{recordsFiltrati.length} record{regione || comando || search || filtroTurno || filtroStato ? ` (${records.length} totali)` : ""}</div>

      <BarraAzioniMassive selezione={sel} nomeSingolare="record" nomePlurale="record">
        <button className="btn btn-secondary btn-sm" onClick={() => bulkAttivo(true)}>
          <CheckCircle size={14} /> Attiva ({sel.count})
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => bulkAttivo(false)}>
          <X size={14} /> Disattiva ({sel.count})
        </button>
        <button className="btn btn-secondary btn-sm text-red-400" onClick={bulkDelete}>
          <Trash2 size={14} /> Elimina ({sel.count})
        </button>
      </BarraAzioniMassive>

      {/* Table */}
      {loading ? <div className="text-slate-400 text-sm">Caricamento...</div> : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="p-3 w-8"><CheckboxTutte selezione={sel} /></th>
                <th className="text-left p-3 text-slate-400 font-medium">Cognome Nome</th>
                <th className="text-left p-3 text-slate-400 font-medium">Qual.</th>
                <th className="text-left p-3 text-slate-400 font-medium">Regione</th>
                <th className="text-left p-3 text-slate-400 font-medium">Comando / Sede</th>
                <th className="text-left p-3 text-slate-400 font-medium">Turno</th>
                <th className="text-left p-3 text-slate-400 font-medium">Telefono</th>
                <th className="text-left p-3 text-slate-400 font-medium">Email</th>
                <th className="text-left p-3 text-slate-400 font-medium">Dal</th>
                <th className="text-left p-3 text-slate-400 font-medium">Stato</th>
                <th className="text-left p-3 text-slate-400 font-medium">Registrato</th>
                <th className="text-left p-3 text-slate-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {recordsFiltrati.map(r => (
                <tr key={r.id} className={`border-b border-slate-800 hover:bg-slate-800/50 ${sel.isSel(r.id) ? "bg-slate-800/70" : ""}`}>
                  <td className="p-3"><CheckboxRiga selezione={sel} id={r.id} /></td>
                  <td className="p-3 text-white font-medium">{r.cognome} {r.nome}</td>
                  <td className="p-3 text-slate-300 text-xs">{r.qualifica || "–"}</td>
                  <td className="p-3 text-slate-300 text-xs">{r.regione || "–"}</td>
                  <td className="p-3 text-slate-300 text-xs">
                    <div>{r.comando}</div>
                    {r.sede && <div className="text-slate-500">{r.sede}</div>}
                  </td>
                  <td className="p-3 text-slate-300 text-xs">{r.turno || "–"}{r.salto ? `/${r.salto}` : ""}</td>
                  <td className="p-3 text-slate-300 text-xs font-mono">{r.telefono || "–"}</td>
                  <td className="p-3 text-xs font-mono">
                    {r.email
                      ? <span className="text-blue-400">{r.email}</span>
                      : <span className="text-slate-500 italic">n/d</span>}
                  </td>
                  <td className="p-3 text-slate-300 text-xs">{r.tas2_dal || "–"}</td>
                  <td className="p-3">
                    {r.attivo
                      ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/40">Attivo</span>
                      : <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/40">Inattivo</span>}
                  </td>
                  <td className="p-3">
                    {/* Flag calcolato live dal backend sull'email (come Sedi/Enti):
                        `operatore_id` diceva "Sì" per sempre anche ad account cancellato. */}
                    {r.operatore_registrato
                      ? <span className="flex items-center gap-1 text-xs text-green-400"><CheckCircle size={12} /> Sì</span>
                      : <span className="text-xs text-slate-500">No</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button className="btn btn-secondary btn-sm" title="Modifica" onClick={() => openEdit(r)}><Pencil size={13} /></button>
                      <button className="btn btn-sm bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/40" title="Elimina" onClick={() => handleDelete(r.id, `${r.cognome} ${r.nome}`)}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {records.length === 0 && (
                <tr><td colSpan={12} className="p-6 text-center text-slate-500">Nessun record trovato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="tactical-card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold">{modal.mode === "create" ? "Aggiungi Personale TAS2" : "Modifica Personale TAS2"}</h3>
              <button className="text-slate-400 hover:text-white" onClick={closeModal}><X size={18} /></button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <label className="form-label">Cognome *</label>
                <input className="form-input" style={{ textTransform: "uppercase" }} value={form.cognome} onChange={e => setForm(p => ({ ...p, cognome: fmtCognome(e.target.value) }))} placeholder="ROSSI" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="form-label">Nome *</label>
                <input className="form-input" value={form.nome} onChange={f("nome")} placeholder="Mario" />
              </div>
              <div>
                <label className="form-label">Qualifica</label>
                <input className="form-input" value={form.qualifica} onChange={f("qualifica")} placeholder="VF, VE, CS…" />
              </div>
              <div>
                <label className="form-label">Regione</label>
                <select className="form-input" value={form.regione}
                  onChange={e => setForm(p => ({ ...p, regione: e.target.value, comando: "", sede: "" }))}>
                  <option value="">— seleziona —</option>
                  {VVF_REGIONI.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Comando</label>
                <select className="form-input" value={form.comando}
                  onChange={e => setForm(p => ({ ...p, comando: e.target.value, sede: "" }))}>
                  <option value="">— seleziona —</option>
                  {getComandi(form.regione).filter(c => c !== "").map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Sede</label>
                <select className="form-input" value={form.sede} onChange={f("sede")}>
                  <option value="">— seleziona —</option>
                  {getSedi(form.regione, form.comando).map(s => <option key={s} value={s}>{s}</option>)}
                  {form.sede && !getSedi(form.regione, form.comando).includes(form.sede) && (
                    <option value={form.sede}>{form.sede}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="form-label">Turno</label>
                <select className="form-input" value={form.turno} onChange={f("turno")}>
                  <option value="">— seleziona —</option>
                  {TURNI_TAS2.map(t => <option key={t} value={t}>{t}</option>)}
                  {form.turno && !TURNI_TAS2.includes(form.turno) && <option value={form.turno}>{form.turno}</option>}
                </select>
              </div>
              <div>
                <label className="form-label">Salto</label>
                <select className="form-input" value={form.salto} onChange={f("salto")}>
                  <option value="">— seleziona —</option>
                  {SALTI_TAS2.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Telefono</label>
                <input className="form-input" value={form.telefono} onChange={f("telefono")} placeholder="3xx…" />
              </div>
              <div>
                <label className="form-label">TAS2 dal</label>
                <input className="form-input" type="number" min="2000" max="2099" value={form.tas2_dal} onChange={f("tas2_dal")} placeholder="2019" />
              </div>
              <div className="col-span-2">
                <label className="form-label">Email istituzionale</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value.toLowerCase() }))} placeholder="mario.rossi@vigilfuoco.it" />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input id="attivo-check" type="checkbox" checked={form.attivo} onChange={f("attivo")} className="w-4 h-4 accent-orange-500" />
                <label htmlFor="attivo-check" className="text-sm text-slate-300 cursor-pointer">Attivo (autorizzato alla registrazione)</label>
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-slate-700">
              <button className="btn btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Salvataggio…" : modal.mode === "create" ? "Aggiungi" : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal anteprima import Excel */}
      {importPreview && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="tactical-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold">Anteprima importazione TAS2</h3>
              <button className="text-slate-400 hover:text-white" onClick={() => setImportPreview(null)}><X size={18} /></button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 rounded-lg text-center" style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)" }}>
                <div className="text-2xl font-bold text-green-400">{importPreview.valid.length}</div>
                <div className="text-xs text-green-300">Validi — saranno importati</div>
              </div>
              <div className="p-3 rounded-lg text-center" style={{ background: importPreview.errors.length ? "rgba(239,68,68,0.1)" : "rgba(100,116,139,0.1)", border: importPreview.errors.length ? "1px solid rgba(239,68,68,0.3)" : "1px solid rgba(100,116,139,0.2)" }}>
                <div className={`text-2xl font-bold ${importPreview.errors.length ? "text-red-400" : "text-slate-500"}`}>{importPreview.errors.length}</div>
                <div className={`text-xs ${importPreview.errors.length ? "text-red-300" : "text-slate-500"}`}>Con errori — non importati</div>
              </div>
            </div>

            {importPreview.truncated && (
              <p className="text-xs text-yellow-400 mb-3">⚠ Superato il limite di 1000 righe: verranno importate solo le prime 1000.</p>
            )}

            {importPreview.valid.length > 0 && (
              <div className="overflow-x-auto mb-3 max-h-56 overflow-y-auto border border-slate-700 rounded">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-800">
                    <tr className="text-slate-400">
                      <th className="text-left p-2">Cognome Nome</th><th className="text-left p-2">Qual.</th>
                      <th className="text-left p-2">Regione</th><th className="text-left p-2">Comando / Sede</th>
                      <th className="text-left p-2">Turno / Salto</th><th className="text-left p-2">Telefono</th>
                      <th className="text-left p-2">Email</th><th className="text-left p-2">Dal</th>
                      <th className="text-left p-2">Attivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.valid.slice(0, 50).map((r, i) => (
                      <tr key={i} className="border-t border-slate-800 text-slate-300">
                        <td className="p-2 text-white">{r.cognome} {r.nome}</td><td className="p-2">{r.qualifica || "–"}</td>
                        <td className="p-2">{r.regione || "–"}</td><td className="p-2">{r.comando}{r.sede ? ` / ${r.sede}` : ""}</td>
                        <td className="p-2">{r.turno || "–"}{r.salto ? `/${r.salto}` : ""}</td><td className="p-2 font-mono">{r.telefono || "–"}</td>
                        <td className="p-2 font-mono">{r.email || "–"}</td><td className="p-2">{r.tas2_dal || "–"}</td>
                        <td className="p-2">{r.attivo ? "Sì" : "No"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {importPreview.valid.length > 50 && <p className="text-xs text-slate-500 p-2">…e altri {importPreview.valid.length - 50}</p>}
              </div>
            )}

            {importPreview.errors.length > 0 && (
              <div className="mb-3 max-h-40 overflow-y-auto">
                <p className="text-xs text-red-400 font-medium mb-1">Righe scartate:</p>
                {importPreview.errors.slice(0, 30).map((e, i) => (
                  <p key={i} className="text-xs text-red-300">Riga {e.riga}: {e.motivo}</p>
                ))}
                {importPreview.errors.length > 30 && <p className="text-xs text-red-500">…e altri {importPreview.errors.length - 30}</p>}
              </div>
            )}

            <div className="flex gap-2 justify-between mt-5 pt-4 border-t border-slate-700">
              <button className="btn btn-secondary btn-sm" onClick={downloadImportTemplate}><FileDown size={14} /> Template</button>
              <div className="flex gap-2">
                <button className="btn btn-secondary" onClick={() => setImportPreview(null)}>Annulla</button>
                <button className="btn btn-primary" onClick={handleConfirmImport} disabled={importing || importPreview.valid.length === 0}>
                  {importing ? "Importazione…" : `Importa ${importPreview.valid.length}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Report Tab ────────────────────────────────────────────────────────────────
// Grafici e layout del report: spostati nel componente condiviso ReportView.
const ReportTab = ({ api, logoBase64 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (periodo) => {
    setLoading(true);
    try {
      const res = await api.getReport(periodo);
      setData(res.data);
    } catch {
      toast.error("Errore caricamento report");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Solo al PRIMO caricamento: sostituire il report a ogni refetch smonterebbe
  // ReportView e con esso lo stato del filtro (le date selezionate sparivano).
  if (loading && !data) return <div className="text-slate-400 text-sm text-center py-20">Caricamento report…</div>;
  if (!data)   return <div className="text-red-400 text-sm text-center py-20">Errore nel caricamento dei dati.</div>;

  return (
    <div className="max-w-5xl mx-auto">
      <ReportView
        data={data}
        title="Report Generale"
        subtitle="Riepilogo aggregato di tutte le operazioni nel sistema"
        onRefresh={load}
        loading={loading}
        logoBase64={logoBase64}
        isNational
      />
    </div>
  );
};

// ── SediVvfTab ────────────────────────────────────────────────────────────────
const EMPTY_SEDE = { regione: "", comando: "", sede: "", telefono: "", email: "", attivo: true };

const SediVvfTab = ({ api }) => {
  const [records, setRecords] = useState([]);
  const [filtroRegione, setFiltroRegione] = useState("");
  const [filtroComando, setFiltroComando] = useState("");
  const [filtroStato, setFiltroStato]     = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(null); // null | { mode: "create"|"edit", id? }
  const [form, setForm]       = useState(EMPTY_SEDE);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getSediVvf();
      setRecords(data);
    } catch { toast.error("Errore caricamento Sedi VVF"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...EMPTY_SEDE }); setModal({ mode: "create" }); };
  const openEdit   = (r)  => {
    setForm({ regione: r.regione || "", comando: r.comando || "", sede: r.sede || "",
              telefono: r.telefono || "", email: r.email || "", attivo: r.attivo !== false });
    setModal({ mode: "edit", id: r.id });
  };
  const closeModal = () => { setModal(null); setSaving(false); };

  const fs = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSave = async () => {
    if (!form.regione || !form.comando) { toast.error("Regione e Comando obbligatori"); return; }
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await api.createSediVvf(form);
        toast.success("Sede VVF aggiunta");
      } else {
        await api.updateSediVvf(modal.id, form);
        toast.success("Sede VVF aggiornata");
      }
      closeModal(); load();
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, label) => {
    if (!(await confirmDialog({ title: "Elimina sede", message: `Eliminare sede "${label}"?`, danger: true, confirmLabel: "Elimina" }))) return;
    try { await api.deleteSediVvf(id); toast.success("Sede eliminata"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };


  const bulkDelete = async () => {
    const n = sel.count;
    if (!(await confirmDialog({
      title: "Elimina sedi",
      message: `Eliminare ${n} sed${n === 1 ? "e" : "i"}? Le utenze di sede collegate perdono il riferimento a Regione e Comando.`,
      danger: true, confirmLabel: "Elimina",
    }))) return;
    const { ok, ko } = await eseguiAzioneMassiva(sel.ids, (id) => api.deleteSediVvf(id));
    if (ok) toast.success(`${ok} eliminat${ok === 1 ? "a" : "e"}`);
    if (ko) toast.error(`${ko} non eliminat${ko === 1 ? "a" : "e"}`);
    sel.pulisci(); load();
  };

  // Derive comandi available for the selected regione in the form
  const comandiForm = getComandi(form.regione).filter(c => c !== "");

  const recordsFiltrati = records.filter(r =>
    (!filtroRegione || (r.regione || "").toLowerCase() === filtroRegione.toLowerCase()) &&
    (!filtroComando || (r.comando || "").toUpperCase() === filtroComando.toUpperCase()) &&
    (!filtroStato || (filtroStato === "attivo" ? r.attivo !== false : r.attivo === false))
  );

  const sel = useSelezioneMultipla(records, recordsFiltrati);

  const bulkAttivo = async (attivo) => {
    const { ok, ko } = await eseguiAzioneMassiva(sel.ids, (id) => api.updateSediVvf(id, { attivo }));
    if (ok) {
      const cosa = ok === 1 ? "sede" : "sedi";
      const come = attivo ? (ok === 1 ? "attivata" : "attivate") : (ok === 1 ? "disattivata" : "disattivate");
      toast.success(`${ok} ${cosa} ${come}`);
    }
    if (ko) toast.error(`${ko} non aggiornat${ko === 1 ? "a" : "e"}`);
    sel.pulisci(); load();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h2 className="text-lg font-bold text-white flex-1">Sedi VVF — Direzioni Regionali e CON</h2>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /></button>
          <button className="btn btn-primary btn-sm" onClick={openCreate}><Plus size={14} /> Aggiungi</button>
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Whitelist delle sedi VVF assegnate agli operatori con ruolo <strong className="text-slate-300">DIR_REG</strong> o <strong className="text-slate-300">CON</strong>.
        La regione e il comando di questi utenti possono essere modificati solo da qui.
      </p>

      <FilterBar onReset={() => { setFiltroRegione(""); setFiltroComando(""); setFiltroStato(""); }}>
        <FilterSelect label="Regione" value={filtroRegione} onChange={v => { setFiltroRegione(v); setFiltroComando(""); }}
          options={VVF_REGIONI.filter(r => r !== "Nazionale")}
          placeholder="Tutte le regioni" />
        <FilterSelect label="Comando" value={filtroComando} onChange={setFiltroComando}
          options={filtroRegione ? getComandi(filtroRegione).filter(c => c !== "") : VVF_REGIONI.flatMap(r => getComandi(r).filter(c => c !== ""))}
          placeholder="Tutti i comandi" />
        <FilterSelect label="Stato" value={filtroStato} onChange={setFiltroStato}
          options={[{ value: "attivo", label: "Attiva" }, { value: "inattivo", label: "Inattiva" }]}
          placeholder="Tutti" />
      </FilterBar>

      <div className="text-xs text-slate-400 mb-3">{recordsFiltrati.length} sed{recordsFiltrati.length === 1 ? "e" : "i"}{filtroRegione || filtroComando || filtroStato ? ` (${records.length} totali)` : " registrate"}</div>

      <BarraAzioniMassive selezione={sel} nomeSingolare="sede" nomePlurale="sedi">
        <button className="btn btn-secondary btn-sm" onClick={() => bulkAttivo(true)}>
          <CheckCircle size={14} /> Attiva ({sel.count})
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => bulkAttivo(false)}>
          <X size={14} /> Disattiva ({sel.count})
        </button>
        <button className="btn btn-secondary btn-sm text-red-400" onClick={bulkDelete}>
          <Trash2 size={14} /> Elimina ({sel.count})
        </button>
      </BarraAzioniMassive>

      {loading ? <div className="text-slate-400 text-sm">Caricamento...</div> : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="p-3 w-8"><CheckboxTutte selezione={sel} /></th>
                <th className="text-left p-3 text-slate-400 font-medium">Regione</th>
                <th className="text-left p-3 text-slate-400 font-medium">Comando</th>
                <th className="text-left p-3 text-slate-400 font-medium">Telefono</th>
                <th className="text-left p-3 text-slate-400 font-medium">Email</th>
                <th className="text-left p-3 text-slate-400 font-medium">Stato</th>
                <th className="text-left p-3 text-slate-400 font-medium">Registrato</th>
                <th className="text-left p-3 text-slate-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {recordsFiltrati.map(r => (
                <tr key={r.id} className={`border-b border-slate-800 hover:bg-slate-800/50 ${sel.isSel(r.id) ? "bg-slate-800/70" : ""}`}>
                  <td className="p-3"><CheckboxRiga selezione={sel} id={r.id} /></td>
                  <td className="p-3 text-slate-300 text-xs">{r.regione || "–"}</td>
                  <td className="p-3 text-white font-medium text-xs">{r.comando}</td>
                  <td className="p-3 text-slate-300 text-xs font-mono">{r.telefono || "–"}</td>
                  <td className="p-3 text-xs">
                    {r.email ? <span className="text-blue-400 font-mono">{r.email}</span>
                             : <span className="text-slate-500 italic">n/d</span>}
                  </td>
                  <td className="p-3">
                    {r.attivo
                      ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/40">Attiva</span>
                      : <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/40">Inattiva</span>}
                  </td>
                  <td className="p-3">
                    {r.operatore_registrato
                      ? <span className="flex items-center gap-1 text-xs text-green-400"><CheckCircle size={12} /> Sì</span>
                      : <span className="text-xs text-slate-500">No</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button className="btn btn-secondary btn-sm" title="Modifica" onClick={() => openEdit(r)}><Pencil size={13} /></button>
                      <button className="btn btn-sm bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/40" title="Elimina"
                        onClick={() => handleDelete(r.id, `${r.comando} (${r.regione})`)}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {recordsFiltrati.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-500">Nessuna sede registrata</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="tactical-card w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold">{modal.mode === "create" ? "Aggiungi Sede VVF" : "Modifica Sede VVF"}</h3>
              <button className="text-slate-400 hover:text-white" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="form-label">Regione *</label>
                <select className="form-input" value={form.regione}
                  onChange={e => setForm(p => ({ ...p, regione: e.target.value, comando: "" }))}>
                  <option value="">— seleziona —</option>
                  {VVF_REGIONI.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Comando *</label>
                <select className="form-input" value={form.comando}
                  onChange={e => setForm(p => ({ ...p, comando: e.target.value }))}>
                  <option value="">— seleziona —</option>
                  {comandiForm.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Telefono</label>
                <input className="form-input" value={form.telefono} onChange={fs("telefono")} placeholder="+39 0823 000000" />
              </div>
              <div>
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={fs("email")} placeholder="dir.campania@vigilfuoco.it" />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input id="sede-attiva" type="checkbox" checked={form.attivo} onChange={fs("attivo")} className="w-4 h-4 accent-orange-500" />
                <label htmlFor="sede-attiva" className="text-slate-300 text-sm cursor-pointer">Sede attiva</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="btn btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Salvataggio…" : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── EntiTab ─────────────────────────────────────────────────────────────────────
const EMPTY_ENTE = { regione: "", ente: "", telefono: "", email: "", attivo: true };

const EntiTab = ({ api }) => {
  const [records, setRecords] = useState([]);
  const [filtroRegione, setFiltroRegione] = useState("");
  const [filtroEnte, setFiltroEnte]       = useState("");
  const [filtroStato, setFiltroStato]     = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(null); // null | { mode, id? }
  const [form, setForm]       = useState(EMPTY_ENTE);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getEnti();
      setRecords(data);
    } catch { toast.error("Errore caricamento Enti"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...EMPTY_ENTE }); setModal({ mode: "create" }); };
  const openEdit   = (r)  => {
    setForm({ regione: r.regione || "", ente: r.ente || "",
              telefono: r.telefono || "", email: r.email || "", attivo: r.attivo !== false });
    setModal({ mode: "edit", id: r.id });
  };
  const closeModal = () => { setModal(null); setSaving(false); };

  const fs = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSave = async () => {
    if (!form.regione || !form.ente.trim()) { toast.error("Regione ed Ente obbligatori"); return; }
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await api.createEnte(form);
        toast.success("Ente aggiunto");
      } else {
        await api.updateEnte(modal.id, form);
        toast.success("Ente aggiornato");
      }
      closeModal(); load();
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, label) => {
    if (!(await confirmDialog({ title: "Elimina ente", message: `Eliminare ente "${label}"?`, danger: true, confirmLabel: "Elimina" }))) return;
    try { await api.deleteEnte(id); toast.success("Ente eliminato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const recordsFiltrati = records.filter(r =>
    (!filtroRegione || (r.regione || "").toLowerCase() === filtroRegione.toLowerCase()) &&
    (!filtroEnte || (r.ente || "").toLowerCase().includes(filtroEnte.toLowerCase())) &&
    (!filtroStato || (filtroStato === "attivo" ? r.attivo !== false : r.attivo === false))
  );

  const sel = useSelezioneMultipla(records, recordsFiltrati);

  const bulkAttivo = async (attivo) => {
    const { ok, ko } = await eseguiAzioneMassiva(sel.ids, (id) => api.updateEnte(id, { attivo }));
    if (ok) toast.success(`${ok} ent${ok === 1 ? "e" : "i"} ${attivo ? "attivat" : "disattivat"}${ok === 1 ? "o" : "i"}`);
    if (ko) toast.error(`${ko} non aggiornat${ko === 1 ? "o" : "i"}`);
    sel.pulisci(); load();
  };

  const bulkDelete = async () => {
    const n = sel.count;
    if (!(await confirmDialog({
      title: "Elimina enti",
      message: `Eliminare ${n} ent${n === 1 ? "e" : "i"}? Le email che erano autorizzate solo da qui non potranno più registrarsi.`,
      danger: true, confirmLabel: "Elimina",
    }))) return;
    const { ok, ko } = await eseguiAzioneMassiva(sel.ids, (id) => api.deleteEnte(id));
    if (ok) toast.success(`${ok} eliminat${ok === 1 ? "o" : "i"}`);
    if (ko) toast.error(`${ko} non eliminat${ko === 1 ? "o" : "i"}`);
    sel.pulisci(); load();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h2 className="text-lg font-bold text-white flex-1">Enti — utenze autorizzate</h2>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /></button>
          <button className="btn btn-primary btn-sm" onClick={openCreate}><Plus size={14} /> Aggiungi</button>
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Whitelist delle utenze degli enti autorizzate alla registrazione (oltre a Personale TAS2 e Sedi VVF).
        Un'email presente e attiva qui può registrarsi anche con dominio diverso da <strong className="text-slate-300">@vigilfuoco.it</strong>.
      </p>

      <FilterBar onReset={() => { setFiltroRegione(""); setFiltroEnte(""); setFiltroStato(""); }}>
        <FilterSelect label="Regione" value={filtroRegione} onChange={setFiltroRegione}
          options={VVF_REGIONI.filter(r => r !== "Nazionale")}
          placeholder="Tutte le regioni" />
        <FilterInput label="Ente" value={filtroEnte} onChange={setFiltroEnte} placeholder="Cerca ente…" />
        <FilterSelect label="Stato" value={filtroStato} onChange={setFiltroStato}
          options={[{ value: "attivo", label: "Attivo" }, { value: "inattivo", label: "Inattivo" }]}
          placeholder="Tutti" />
      </FilterBar>

      <div className="text-xs text-slate-400 mb-3">{recordsFiltrati.length} ent{recordsFiltrati.length === 1 ? "e" : "i"}{filtroRegione || filtroEnte || filtroStato ? ` (${records.length} totali)` : " registrat" + (records.length === 1 ? "o" : "i")}</div>

      <BarraAzioniMassive selezione={sel} nomeSingolare="ente" nomePlurale="enti">
        <button className="btn btn-secondary btn-sm" onClick={() => bulkAttivo(true)}>
          <CheckCircle size={14} /> Attiva ({sel.count})
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => bulkAttivo(false)}>
          <X size={14} /> Disattiva ({sel.count})
        </button>
        <button className="btn btn-secondary btn-sm text-red-400" onClick={bulkDelete}>
          <Trash2 size={14} /> Elimina ({sel.count})
        </button>
      </BarraAzioniMassive>

      {loading ? <div className="text-slate-400 text-sm">Caricamento...</div> : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="p-3 w-8"><CheckboxTutte selezione={sel} /></th>
                <th className="text-left p-3 text-slate-400 font-medium">Regione</th>
                <th className="text-left p-3 text-slate-400 font-medium">Ente</th>
                <th className="text-left p-3 text-slate-400 font-medium">Telefono</th>
                <th className="text-left p-3 text-slate-400 font-medium">Email</th>
                <th className="text-left p-3 text-slate-400 font-medium">Stato</th>
                <th className="text-left p-3 text-slate-400 font-medium">Registrato</th>
                <th className="text-left p-3 text-slate-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {recordsFiltrati.map(r => (
                <tr key={r.id} className={`border-b border-slate-800 hover:bg-slate-800/50 ${sel.isSel(r.id) ? "bg-slate-800/70" : ""}`}>
                  <td className="p-3"><CheckboxRiga selezione={sel} id={r.id} /></td>
                  <td className="p-3 text-slate-300 text-xs">{r.regione || "–"}</td>
                  <td className="p-3 text-white font-medium text-xs">{r.ente}</td>
                  <td className="p-3 text-slate-300 text-xs font-mono">{r.telefono || "–"}</td>
                  <td className="p-3 text-xs">
                    {r.email ? <span className="text-blue-400 font-mono">{r.email}</span>
                             : <span className="text-slate-500 italic">n/d</span>}
                  </td>
                  <td className="p-3">
                    {r.attivo
                      ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/40">Attivo</span>
                      : <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/40">Inattivo</span>}
                  </td>
                  <td className="p-3">
                    {r.operatore_registrato
                      ? <span className="flex items-center gap-1 text-xs text-green-400"><CheckCircle size={12} /> Sì</span>
                      : <span className="text-xs text-slate-500">No</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button className="btn btn-secondary btn-sm" title="Modifica" onClick={() => openEdit(r)}><Pencil size={13} /></button>
                      <button className="btn btn-sm bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/40" title="Elimina"
                        onClick={() => handleDelete(r.id, `${r.ente} (${r.regione})`)}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {recordsFiltrati.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-500">Nessun ente registrato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="tactical-card w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold">{modal.mode === "create" ? "Aggiungi Ente" : "Modifica Ente"}</h3>
              <button className="text-slate-400 hover:text-white" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="form-label">Regione *</label>
                <select className="form-input" value={form.regione}
                  onChange={e => setForm(p => ({ ...p, regione: e.target.value }))}>
                  <option value="">— seleziona —</option>
                  {VVF_REGIONI.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Ente *</label>
                <input className="form-input" value={form.ente} onChange={fs("ente")} placeholder="Es. Soccorso Alpino" />
              </div>
              <div>
                <label className="form-label">Telefono</label>
                <input className="form-input" value={form.telefono} onChange={fs("telefono")} placeholder="+39 0823 000000" />
              </div>
              <div>
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={fs("email")} placeholder="referente@ente.it" />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input id="ente-attivo" type="checkbox" checked={form.attivo} onChange={fs("attivo")} className="w-4 h-4 accent-orange-500" />
                <label htmlFor="ente-attivo" className="text-slate-300 text-sm cursor-pointer">Ente attivo</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="btn btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Salvataggio…" : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Errori runtime del frontend ──────────────────────────────────────────────
// Elenco AGGREGATO per impronta, ordinato per ultima occorrenza: quello che sta
// succedendo adesso prima di quello che capitava a marzo. Serve a trovare e
// correggere i guasti, non a fare statistiche — per questo ci sono stack e
// dispositivo, e NON ci sono email, nomi o contenuto dei campi compilati.
const ErroriTab = ({ api }) => {
  const [errori, setErrori]   = useState([]);
  const [aperti, setAperti]   = useState(0);
  const [total, setTotal]     = useState(0);
  const [pages, setPages]     = useState(1);
  const [page, setPage]       = useState(1);
  const [soloAperti, setSoloAperti] = useState(true);
  const [loading, setLoading] = useState(false);
  const [espanso, setEspanso] = useState(null);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getErrori({ solo_aperti: soloAperti, page, page_size: 50 });
      setErrori(data.data || []);
      setAperti(data.aperti || 0);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      toast.error(apiErr(err, "Errore nel caricamento"));
    } finally {
      setLoading(false);
    }
  }, [api, soloAperti, page]);

  useEffect(() => { carica(); }, [carica]);

  const segna = async (imp, risolto) => {
    try {
      await api.setErroreRisolto(imp, risolto);
      carica();
    } catch (err) { toast.error(apiErr(err, "Operazione non riuscita")); }
  };

  const elimina = async (imp) => {
    if (!(await confirmDialog({
      title: "Eliminare questa riga?",
      message: "Se l'errore si ripresenta torna da sola, col contatore che riparte da 1.",
      confirmText: "Elimina",
    }))) return;
    try {
      await api.deleteErrore(imp);
      toast.success("Riga eliminata");
      carica();
    } catch (err) { toast.error(apiErr(err, "Eliminazione non riuscita")); }
  };

  const quando = (v) => (v ? new Date(v).toLocaleString("it-IT") : "—");

  return (
    <div data-testid="errori-tab">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-100">Errori runtime</h2>
          <p className="text-xs text-slate-400">
            {aperti} {aperti === 1 ? "aperto" : "aperti"} · {total} in elenco
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className={`btn btn-sm ${soloAperti ? "btn-primary" : "btn-secondary"}`}
            onClick={() => { setSoloAperti(true); setPage(1); }}
          >Aperti</button>
          <button
            className={`btn btn-sm ${soloAperti ? "btn-secondary" : "btn-primary"}`}
            onClick={() => { setSoloAperti(false); setPage(1); }}
          >Tutti</button>
          <button className="btn btn-sm btn-secondary" onClick={carica} title="Ricarica">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-400">Caricamento…</p>}
      {!loading && errori.length === 0 && (
        <p className="text-sm text-slate-400">
          {soloAperti ? "Nessun errore aperto. " : "Nessun errore registrato. "}
          È la condizione che si spera di vedere.
        </p>
      )}

      <div className="space-y-2">
        {errori.map((e) => (
          <div key={e.impronta}
               className={`rounded-lg border p-3 ${e.risolto ? "border-slate-700 bg-slate-900/40" : "border-amber-700/60 bg-amber-950/20"}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-slate-100 break-words">{e.messaggio}</p>
                <p className="text-xs text-slate-400 mt-1">
                  <span className="font-mono">{e.percorso || "—"}</span>
                  {" · "}{e.contesto}
                  {" · "}<strong>{e.occorrenze}</strong> {e.occorrenze === 1 ? "volta" : "volte"}
                  {" · ultima "}{quando(e.ultimo_il)}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  prima {quando(e.primo_il)}
                  {e.versione ? <> · commit <span className="font-mono">{String(e.versione).slice(0, 8)}</span></> : null}
                  {e.ultimo_utente_nome ? <> · {e.ultimo_utente_nome}</> : <> · utente non autenticato</>}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-none">
                <button className="btn btn-sm btn-secondary"
                        onClick={() => segna(e.impronta, !e.risolto)}
                        title={e.risolto ? "Riapri" : "Segna come visto"}>
                  <Check size={14} />
                </button>
                <button className="btn btn-sm btn-secondary text-red-400"
                        onClick={() => elimina(e.impronta)} title="Elimina">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <button className="text-xs text-sky-400 mt-2 underline"
                    onClick={() => setEspanso(espanso === e.impronta ? null : e.impronta)}>
              {espanso === e.impronta ? "Nascondi dettagli" : "Stack e dispositivo"}
            </button>
            {espanso === e.impronta && (
              <div className="mt-2 space-y-2">
                <pre className="text-[11px] bg-slate-950 border border-slate-800 rounded p-2 overflow-auto max-h-64 whitespace-pre-wrap">
                  {e.stack || "(nessuno stack)"}
                </pre>
                <p className="text-[11px] text-slate-500 break-all">{e.user_agent || "(user agent ignoto)"}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button className="btn btn-sm btn-secondary" disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>Precedente</button>
          <span className="text-xs text-slate-400">{page} / {pages}</span>
          <button className="btn btn-sm btn-secondary" disabled={page >= pages}
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}>Successiva</button>
        </div>
      )}
    </div>
  );
};


// ── Main AdminPanel ───────────────────────────────────────────────────────────
// Tab Admin: la voce 'Validazione POA' viene aggiunta solo se il feature
// flag POA_ENABLED e' attivo. Cosi' a POA disattivato sparisce anche dal
// menu Admin (oltre che essere bloccata dal middleware 404 backend).
const IngestKeysTab = ({ api }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nome, setNome] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null); // {nome, key} mostrata UNA volta

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.getIngestKeys(); setKeys(data || []); }
    catch (e) { toast.error(apiErr(e, "Errore caricamento chiavi")); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const n = nome.trim();
    if (!n) { toast.error("Inserisci il nome del comando"); return; }
    setCreating(true);
    try {
      const { data } = await api.createIngestKey(n);
      setNewKey({ nome: data.nome, key: data.key });
      setNome("");
      load();
    } catch (e) { toast.error(apiErr(e, "Errore creazione chiave")); }
    finally { setCreating(false); }
  };
  const revoca = async (k) => {
    if (!(await confirmDialog({ title: "Revoca chiave", message: <>Revocare la chiave di <strong className="text-orange-500">{k.nome}</strong>? Il bridge di quel comando smetterà di inviare posizioni.</>, danger: true, confirmLabel: "Revoca" }))) return;
    try { await api.revocaIngestKey(k.id); toast.success("Chiave revocata"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore revoca")); }
  };
  const elimina = async (k) => {
    if (!(await confirmDialog({ title: "Elimina chiave", message: <>Eliminare definitivamente la chiave di <strong className="text-orange-500">{k.nome}</strong>?</>, danger: true, confirmLabel: "Elimina" }))) return;
    try { await api.deleteIngestKey(k.id); toast.success("Chiave eliminata"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore eliminazione")); }
  };
  const copy = (t) => { try { navigator.clipboard.writeText(t); toast.success("Copiata"); } catch { /* clipboard ko */ } };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Radio size={18} /> Chiavi ingest radio (NMEA/LRRP)</h2>
        <button className="btn btn-secondary btn-sm" onClick={load} title="Ricarica"><RefreshCw size={14} /></button>
      </div>
      <p className="text-xs text-slate-400 mb-4">Una chiave per comando, da inserire nel <code className="text-orange-300">config.json</code> del bridge (.exe) come <code className="text-orange-300">ingest_key</code>. Revocabile singolarmente: il comando revocato non potrà più inviare posizioni a TAS.</p>

      <div className="flex gap-2 mb-4">
        <input className="form-input" placeholder="Nome comando (es. Comando VVF Benevento)" value={nome} onChange={e => setNome(e.target.value)} />
        <button className="btn btn-primary btn-sm whitespace-nowrap" onClick={create} disabled={creating}><Plus size={14} /> {creating ? "Creo…" : "Nuova chiave"}</button>
      </div>

      {newKey && (
        <div className="mb-4 p-4 rounded-lg border border-green-600 bg-green-950/30">
          <p className="text-green-300 font-semibold mb-2 flex items-center gap-2"><KeyRound size={15} /> Chiave per “{newKey.nome}” — <span className="text-green-200">copiala adesso, non sarà più visibile</span></p>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-[200px] break-all bg-slate-900 px-3 py-2 rounded text-orange-300 font-mono text-sm">{newKey.key}</code>
            <button className="btn btn-secondary btn-sm" onClick={() => copy(newKey.key)}>Copia</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setNewKey(null)}>Chiudi</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-slate-400 text-sm">Caricamento...</div> : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-700 text-slate-400 text-left">
              <th className="p-2">Comando</th><th className="p-2">Prefisso</th><th className="p-2">Stato</th><th className="p-2">Creata</th><th className="p-2">Ultimo uso</th><th className="p-2"></th>
            </tr></thead>
            <tbody>
              {keys.length === 0 ? <tr><td colSpan={6} className="p-4 text-center text-slate-500">Nessuna chiave</td></tr> :
                keys.map(k => (
                  <tr key={k.id} className="border-b border-slate-800">
                    <td className="p-2 text-white">{k.nome}</td>
                    <td className="p-2 font-mono text-slate-400">{k.key_prefix}…</td>
                    <td className="p-2">{k.attivo ? <span className="badge badge-green">attiva</span> : <span className="badge badge-gray">revocata</span>}</td>
                    <td className="p-2 text-slate-400 text-xs">{k.created_at ? fmtLogTs(k.created_at).data : "—"}</td>
                    <td className="p-2 text-slate-400 text-xs">{k.last_used_at ? `${fmtLogTs(k.last_used_at).data} ${fmtLogTs(k.last_used_at).ora}` : "mai"}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {k.attivo && <button className="btn btn-secondary btn-sm" onClick={() => revoca(k)} style={{ color: '#fca5a5' }}>Revoca</button>}
                      <button className="btn btn-secondary btn-sm ml-1" onClick={() => elimina(k)} title="Elimina"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Avvisi: bacheca che l'operatore legge dalla campanella in Dashboard ───────
// Un avviso raggiunge SOLO chi si e' registrato prima della pubblicazione, e
// resta visibile sei mesi (vedi routers/avvisi.py): non e' un archivio, e' il
// "cosa e' cambiato da quando non entri".
const AvvisiTab = ({ api }) => {
  const VUOTO = { titolo: "", corpo: "", tipo: "NOVITA", fissato: false };

  const [avvisi, setAvvisi]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm]       = useState(VUOTO);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.getAvvisi(); setAvvisi(data || []); }
    catch (e) { toast.error(apiErr(e, "Errore caricamento avvisi")); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setForm(VUOTO); setEditId(null); };

  const salva = async () => {
    const titolo = form.titolo.trim();
    const corpo  = form.corpo.trim();
    if (!titolo) { toast.error("Il titolo è obbligatorio"); return; }
    if (!corpo)  { toast.error("Il testo è obbligatorio"); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.updateAvviso(editId, { titolo, corpo, tipo: form.tipo, fissato: form.fissato });
        toast.success("Avviso aggiornato");
      } else {
        await api.createAvviso({ titolo, corpo, tipo: form.tipo, fissato: form.fissato });
        toast.success("Avviso pubblicato");
      }
      reset(); load();
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio")); }
    finally { setSaving(false); }
  };

  const modifica = (a) => {
    setEditId(a.id);
    setForm({ titolo: a.titolo, corpo: a.corpo, tipo: a.tipo, fissato: !!a.fissato });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleAttivo = async (a) => {
    try {
      await api.updateAvviso(a.id, { attivo: !a.attivo });
      toast.success(a.attivo ? "Avviso ritirato" : "Avviso ripubblicato");
      load();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const elimina = async (a) => {
    if (!(await confirmDialog({
      title: "Elimina avviso",
      message: <>Eliminare definitivamente <strong className="text-orange-500">{a.titolo}</strong>? Si perde anche traccia di chi lo aveva letto. Per toglierlo dalla vista conservando tutto, usa <strong>Ritira</strong>.</>,
      danger: true, confirmLabel: "Elimina",
    }))) return;
    try { await api.deleteAvviso(a.id); toast.success("Avviso eliminato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore eliminazione")); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Bell size={18} /> Avvisi agli operatori</h2>
        <button className="btn btn-secondary btn-sm" onClick={load} title="Ricarica"><RefreshCw size={14} /></button>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Compaiono nella campanella in Dashboard. Raggiungono solo chi si è registrato
        <strong className="text-slate-300"> prima</strong> della pubblicazione e restano visibili
        <strong className="text-slate-300"> sei mesi</strong>: chi si iscrive domani non si troverà
        mesi di arretrati da leggere.
      </p>

      {/* Composizione */}
      <div className="tactical-card p-4 mb-5">
        <div className="text-white font-medium mb-3 text-sm">
          {editId ? "Modifica avviso" : "Nuovo avviso"}
        </div>
        <div className="grid md:grid-cols-[1fr,200px] gap-3 mb-3">
          <div>
            <label className="form-label">Titolo *</label>
            <input className="form-input" value={form.titolo} maxLength={120}
              onChange={e => setForm({ ...form, titolo: e.target.value })}
              placeholder="Es. Nuova pagina Dispositivi: GPS e radio" />
          </div>
          <div>
            <label className="form-label">Tipo</label>
            <select className="form-input" value={form.tipo}
              onChange={e => setForm({ ...form, tipo: e.target.value })}>
              {TIPI_AVVISO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Testo *</label>
          <textarea className="form-input resize-none" rows={5} maxLength={4000}
            value={form.corpo} onChange={e => setForm({ ...form, corpo: e.target.value })}
            placeholder={"Cosa è cambiato e cosa deve fare l'operatore.\nScrivi come parleresti a un TAS2 che rientra dopo due settimane."} />
          <div className="text-right text-xs text-slate-500 mt-1">{form.corpo.length}/4000</div>
        </div>
        <label className="flex items-start gap-2 cursor-pointer mb-4">
          <input type="checkbox" className="mt-0.5" checked={form.fissato}
            onChange={e => setForm({ ...form, fissato: e.target.checked })} />
          <span className="text-sm text-slate-300">
            In evidenza — resta in cima anche dopo la lettura
            <span className="text-slate-500 block text-xs">Per un disservizio in corso: l'ordine cronologico lo farebbe scivolare giù proprio mentre serve.</span>
          </span>
        </label>
        <div className="flex gap-2 justify-end border-t border-slate-700 pt-4">
          {editId && <button className="btn btn-secondary" onClick={reset} disabled={saving}>Annulla</button>}
          <button className="btn btn-primary flex items-center gap-2" onClick={salva} disabled={saving}>
            {saving ? <><RefreshCw size={14} className="animate-spin" /> Salvataggio…</>
                    : <><Plus size={14} /> {editId ? "Salva modifiche" : "Pubblica avviso"}</>}
          </button>
        </div>
      </div>

      {/* Elenco */}
      {loading ? (
        <div className="flex items-center justify-center h-32 text-slate-400">
          <RefreshCw size={18} className="animate-spin mr-2" /> Caricamento…
        </div>
      ) : avvisi.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 text-slate-500 gap-2">
          <Bell size={28} />
          <div className="text-sm">Nessun avviso pubblicato.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {avvisi.map(a => (
            <div key={a.id} className={`tactical-card p-4 ${a.attivo ? "" : "opacity-60"}`}>
              <div className="flex items-start gap-2 flex-wrap mb-1.5">
                <span className="px-2 py-0.5 rounded-full border border-slate-600 bg-slate-700/40 text-[11px] text-slate-300">
                  {(TIPI_AVVISO.find(t => t.value === a.tipo) || {}).label || a.tipo}
                </span>
                {a.fissato && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-600 bg-slate-700/40 text-[11px] text-slate-300">
                    <Pin size={11} /> In evidenza
                  </span>
                )}
                {!a.attivo && (
                  <span className="px-2 py-0.5 rounded-full bg-red-900/40 text-red-400 border border-red-800/50 text-[11px]">
                    ritirato
                  </span>
                )}
                <span className="text-xs text-slate-500 ml-auto">
                  {a.created_at ? new Date(a.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : ""}
                  {a.creato_da_email ? ` · ${a.creato_da_email}` : ""}
                </span>
              </div>
              <div className="text-white font-medium text-sm">{a.titolo}</div>
              <div className="text-slate-400 text-sm mt-1 whitespace-pre-line line-clamp-3">{a.corpo}</div>
              <div className="flex gap-1.5 justify-end mt-3">
                <button className="btn btn-secondary btn-sm flex items-center gap-1" onClick={() => modifica(a)}>
                  <Pencil size={13} /> Modifica
                </button>
                <button className="btn btn-secondary btn-sm flex items-center gap-1" onClick={() => toggleAttivo(a)}
                  title={a.attivo ? "Togli dalla vista degli operatori, conservando chi l'ha letto" : "Rimetti in linea"}>
                  {a.attivo ? <><EyeOff size={13} /> Ritira</> : <><Eye size={13} /> Ripubblica</>}
                </button>
                <button className="btn btn-sm bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/40"
                  onClick={() => elimina(a)} title="Elimina definitivamente">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TABS = [
  { id: "dashboard",       label: "Dashboard",       icon: LayoutDashboard },
  { id: "utenti",          label: "Utenti",          icon: Users },
  { id: "tas2",            label: "Personale TAS2",  icon: UserCheck },
  { id: "sedi_vvf",        label: "Sedi VVF",        icon: Building2 },
  { id: "enti",            label: "Enti",            icon: Landmark },
  { id: "interventi",      label: "Interventi",      icon: ClipboardList },
  { id: "avvisi",          label: "Avvisi",          icon: Bell },
  { id: "report",          label: "Report",          icon: BarChart2 },
  // Visibile anche a POA disabilitato: la raccolta dei casi prosegue comunque e
  // la tab mostra almeno il contatore di calibrazione (le metriche di
  // validazione richiedono POA attivo).
  { id: "validazione_poa", label: POA_ENABLED ? "Validazione POA" : "Casi POA", icon: Target },
  { id: "logs",            label: "Log Sistema",     icon: FileText },
  { id: "errori",          label: "Errori",          icon: AlertTriangle },
  { id: "ingest_keys",     label: "Chiavi radio",    icon: KeyRound },
  { id: "impostazioni",    label: "Impostazioni",    icon: Settings },
];

const AdminPanel = ({ operatore, token, onBack, operazioneAttiva, logoBase64 }) => {
  const [tab, setTab]           = useState("dashboard");
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed]   = useState(false);
  const [setupDone, setSetupDone] = useState(false);
  const api = createAdminApi(token);

  const handleSetupAdmin = async () => {
    const key = window.prompt("Inserisci la chiave di setup amministratore (SETUP_SECRET):");
    if (!key) return;
    try {
      await api.setupAdmin(key);
      toast.success("Sei ora amministratore! Ricarico...");
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(apiErr(e, "Errore setup"));
    }
  };

  if (operatore?.ruolo !== "admin") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="tactical-card p-8 max-w-md text-center">
          <Shield size={48} className="mx-auto mb-4 text-orange-500" />
          <h2 className="text-xl font-bold text-white mb-2">Accesso Amministratore</h2>
          <p className="text-slate-400 mb-6 text-sm">
            Non hai il ruolo admin. Se sei il primo utente del sistema, puoi promuoverti ad amministratore.
          </p>
          {!setupDone ? (
            <button className="btn btn-primary" onClick={handleSetupAdmin}>
              Diventa Primo Admin
            </button>
          ) : (
            <p className="text-green-400 text-sm">Ruolo assegnato — ricarica la pagina per accedere.</p>
          )}
          <button className="btn btn-secondary w-full mt-3" onClick={onBack}>
            <ArrowLeft size={14} /> Torna all'app
          </button>
        </div>
      </div>
    );
  }

  const ActiveTab = { dashboard: DashboardTab, utenti: UtentiTab, tas2: Tas2PersonaleTab, sedi_vvf: SediVvfTab, enti: EntiTab, interventi: InterventiTab, avvisi: AvvisiTab, report: ReportTab, validazione_poa: ValidazionePoaTab, logs: LogsTab, errori: ErroriTab, ingest_keys: IngestKeysTab, impostazioni: ImpostazioniTab }[tab];

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-4">
        {/* Hamburger — visibile solo su mobile (drawer pattern) */}
        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-300 hover:bg-slate-800"
          onClick={() => setSidebarMobileOpen(true)}
          aria-label="Apri menu"
        >
          <Menu size={20} />
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onBack}><ArrowLeft size={14} /> <span className="hidden sm:inline">App</span></button>
        <div className="flex items-center gap-2 min-w-0">
          {logoBase64
            ? <img src={logoBase64} alt="Logo" className="w-7 h-7 object-contain rounded" />
            : <Shield size={18} className="text-orange-500" />
          }
          <span className="text-white font-bold text-sm sm:text-base truncate">Pannello Amministratore</span>
        </div>
        <div className="ml-auto text-xs text-slate-400 hidden sm:block truncate">{operatore?.email}</div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Backdrop mobile drawer */}
        {sidebarMobileOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-[2px]"
            onClick={() => setSidebarMobileOpen(false)}
          />
        )}

        {/* Sidebar tabs — comportamento:
            • desktop/tablet: collassabile manualmente via toggle (w-48 ↔ w-14)
            • mobile (< md): drawer off-screen, si apre via hamburger */}
        <aside
          className={`
            bg-slate-900 border-r border-slate-800 flex-shrink-0
            fixed md:static inset-y-0 left-0 z-50
            ${sidebarCollapsed ? "w-14" : "w-48"}
            transform transition-all duration-200 ease-out
            ${sidebarMobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
          `}
        >
          {/* Toggle collassa (solo desktop/tablet) */}
          <div className="hidden md:flex justify-end px-2 pt-2">
            <button
              type="button"
              onClick={() => setSidebarCollapsed(c => !c)}
              title={sidebarCollapsed ? "Espandi menu" : "Comprimi menu"}
              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <ChevronLeft size={14} className={`transition-transform duration-200 ${sidebarCollapsed ? "rotate-180" : ""}`} />
            </button>
          </div>

          <nav className="p-3 space-y-1">
            {TABS.map(t => {
              const Icon = t.icon;
              const isActive = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => { setTab(t.id); setSidebarMobileOpen(false); }}
                  title={t.label}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${sidebarCollapsed ? "justify-center" : "justify-start"} ${isActive ? "bg-orange-500/20 text-orange-400 font-medium" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
                >
                  <Icon size={16} className="flex-shrink-0" />
                  {!sidebarCollapsed && <span className="truncate">{t.label}</span>}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-6">
          <ActiveTab api={api} operatoreId={operatore?.id} operazioneAttiva={operazioneAttiva} onNavigate={setTab} logoBase64={logoBase64} />
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
